import { z } from 'zod';
import { BUYBOX_MERKMALE, type BuyBoxMerkmal } from './buybox';

/**
 * Branche je Marktplatz mit Vorlagen für die Produktfelder (Uhren, Schmuck, Mode, Elektronik, Lebensmittel …).
 * Beim Setzen der Branche legt die Middleware die Felder der Vorlage an bzw. ergänzt sie (Herkunft „vorlage“,
 * Namespace „dxl“). Eigene Felder und Felder aus dem Shop bleiben unberührt.
 *
 * Die Marke steht in keiner Vorlage: sie ist das Standardfeld „Marke“ des Produkts (produkt.marke, im Shop „Vendor“)
 * und wird von Import, Buy Box und Shop-Übertragung schon genutzt.
 *
 * Neue Branchen: Schlüssel hier ergänzen und den Check-Constraint mandant.branche per Migration erweitern.
 */

export const BRANCHEN = {
  fashion: 'Mode & Bekleidung',
  schuhe: 'Schuhe',
  uhren: 'Uhren',
  schmuck: 'Schmuck',
  beauty: 'Beauty & Kosmetik',
  elektronik: 'Elektronik & Technik',
  auto: 'Auto & Motorrad',
  moebel: 'Möbel & Wohnen',
  garten: 'Haus, Garten & Heimwerken',
  sport: 'Sport & Fitness',
  outdoor: 'Outdoor & Camping',
  fahrrad: 'Fahrrad & E-Bike',
  spielwaren: 'Spielwaren & Baby',
  tierbedarf: 'Tierbedarf',
  lebensmittel: 'Lebensmittel & Getränke',
  nahrungsergaenzung: 'Nahrungsergänzung & Sporternährung',
  drogerie: 'Drogerie & Gesundheit',
  buecher: 'Bücher, Medien & Büro',
  kunst: 'Kunst, Sammlerstücke & Antiquitäten',
  allgemein: 'Allgemein',
} as const;
export type Branche = keyof typeof BRANCHEN;
export const BRANCHE_LISTE = Object.keys(BRANCHEN) as Branche[];
export const BrancheSchema = z.enum(BRANCHE_LISTE as [Branche, ...Branche[]]);
export const STANDARD_BRANCHE: Branche = 'allgemein';

/** Bereiche für die Auswahl (Reihenfolge der Anzeige) */
export const BRANCHE_BEREICHE = {
  lifestyle: 'Mode & Lifestyle',
  technik: 'Technik & Mobilität',
  freizeit: 'Haus & Freizeit',
  gesundheit: 'Food & Gesundheit',
  weitere: 'Weitere',
} as const;
export type BrancheBereich = keyof typeof BRANCHE_BEREICHE;

export const BRANCHE_BEREICH: Record<Branche, BrancheBereich> = {
  fashion: 'lifestyle', schuhe: 'lifestyle', uhren: 'lifestyle', schmuck: 'lifestyle', beauty: 'lifestyle',
  elektronik: 'technik', auto: 'technik', fahrrad: 'technik',
  moebel: 'freizeit', garten: 'freizeit', sport: 'freizeit', outdoor: 'freizeit', spielwaren: 'freizeit', tierbedarf: 'freizeit',
  lebensmittel: 'gesundheit', nahrungsergaenzung: 'gesundheit', drogerie: 'gesundheit',
  buecher: 'weitere', kunst: 'weitere', allgemein: 'weitere',
};

/** Kurztext für die Kacheln der Auswahl */
export const BRANCHE_KURZ: Record<Branche, string> = {
  fashion: 'Bekleidung, Größe, Textilkennzeichnung',
  schuhe: 'Schuhgröße, Weite, Obermaterial',
  uhren: 'Referenz, Werk, Zustand, Papiere',
  schmuck: 'Legierung, Feingehalt, Steine',
  beauty: 'INCI, Nenninhalt, Haltbarkeit',
  elektronik: 'Energielabel, WEEE, Batterien',
  auto: 'Teilenummer, Fahrzeugeignung',
  moebel: 'Maße, Material, Aufbau',
  garten: 'Werkzeug, Garten, Baumarkt',
  sport: 'Sportart, Größe, Material',
  outdoor: 'Wassersäule, Gewicht, Packmaß',
  fahrrad: 'Rahmengröße, Modelljahr, Akku',
  spielwaren: 'Altersempfehlung, Warnhinweise, CE',
  tierbedarf: 'Tierart, Futter, Zusammensetzung',
  lebensmittel: 'LMIV: Zutaten, Allergene, Nährwerte',
  nahrungsergaenzung: 'Tagesdosis, Verzehrempfehlung',
  drogerie: 'Inhaltsstoffe, Anwendung, Grundpreis',
  buecher: 'ISBN, Autor, Verlag, Format',
  kunst: 'Künstler, Epoche, Provenienz',
  allgemein: 'Ohne Vorlage – eigene Felder',
};

export const BRANCHE_TEXT: Record<Branche, string> = {
  fashion: 'Bekleidung und Accessoires: Größe, Größensystem, Farbe, Materialzusammensetzung nach Textilkennzeichnungsverordnung, Passform und Pflege.',
  schuhe: 'Schuhe: Schuhgröße, Größensystem, Weite, Obermaterial, Futter und Sohle (Schuhkennzeichnung), Absatzhöhe.',
  uhren: 'Luxus- und Gebrauchtuhren: Zustand, Lieferumfang, Referenz, Werk, Gehäuse, Material und Funktionen. EAN ist keine Pflicht.',
  schmuck: 'Schmuck und Edelsteine: Schmuckart, Legierung, Feingehalt, Steine mit Karat und Reinheit, Maße. EAN ist keine Pflicht.',
  beauty: 'Kosmetik und Pflege: INCI-Inhaltsstoffe, Nenninhalt mit Grundpreis, Haltbarkeit nach dem Öffnen (PAO), Anwendung und Warnhinweise.',
  elektronik: 'Elektronik, Computer und Haushaltsgeräte: Hersteller-Teilenummer, Zustand, Energieeffizienzklasse mit Label und Datenblatt, WEEE- und Batterieangaben.',
  auto: 'Teile und Zubehör für Auto und Motorrad: Hersteller- und OE-Teilenummer, Fahrzeugeignung, Einbauposition, Zustand. EAN ist keine Pflicht.',
  moebel: 'Möbel, Leuchten und Wohnaccessoires: Maße, Material, Farbe, Aufbau, Belastbarkeit und Energieeffizienz bei Leuchten. EAN ist keine Pflicht.',
  garten: 'Garten, Werkzeug und Baumarkt: Antrieb, Leistung, Akku, Maße, Material und Sicherheitsangaben.',
  sport: 'Sport- und Fitnessartikel: Sportart, Größe, Farbe, Material, maximale Belastung und Einsatzbereich.',
  outdoor: 'Outdoor und Camping: Wassersäule, Gewicht, Packmaß, Personenzahl, Temperaturbereich und Material.',
  fahrrad: 'Fahrräder, E-Bikes und Zubehör: Modelljahr, Rahmengröße, Laufradgröße, Schaltung, Motor und Akku. EAN ist keine Pflicht.',
  spielwaren: 'Spielzeug und Babyartikel: Altersempfehlung, Warnhinweise nach Spielzeugrichtlinie, CE-Kennzeichnung, Material und Batterien.',
  tierbedarf: 'Futter und Zubehör: Tierart, Lebensphase, Zusammensetzung und analytische Bestandteile, Fütterungsempfehlung, Grundpreis.',
  lebensmittel: 'Lebensmittel und Getränke nach LMIV: Zutaten, Allergene, Nährwerte je 100 g/ml, Nettofüllmenge mit Grundpreis, Lagerung, Lebensmittelunternehmer, Alkoholgehalt.',
  nahrungsergaenzung: 'Nahrungsergänzung und Sporternährung: Nährstoffe je Tagesdosis, Verzehrempfehlung, Pflicht-Warnhinweise, Zutaten, Allergene und Grundpreis.',
  drogerie: 'Drogerie, Haushalt und Gesundheit: Inhaltsstoffe, Anwendung, Warnhinweise und Gefahrenkennzeichnung, Inhalt mit Grundpreis.',
  buecher: 'Bücher, Medien und Bürobedarf: ISBN, Autor, Verlag, Erscheinungsjahr, Sprache, Format und Zustand.',
  kunst: 'Kunst, Antiquitäten und Sammlerstücke: Künstler, Epoche, Technik, Maße, Provenienz, Signatur und Echtheit. EAN ist keine Pflicht.',
  allgemein: 'Ohne Vorlage – Sie legen die Produktfelder selbst an.',
};

/** Namespace der Vorlagenfelder (Metafelder im Marktplatz-Shop: dxl.zustand, dxl.referenz …) */
export const VORLAGE_NAMESPACE = 'dxl';

export const BrancheAendernSchema = z.object({ branche: BrancheSchema });
export type BrancheAendern = z.input<typeof BrancheAendernSchema>;

/** Feld einer Branchenvorlage */
export interface VorlageFeld {
  /** stabiler Schlüssel im Namespace „dxl“ */
  schluessel: string;
  name: string;
  /** Typ wie in produktfeld.typ (PRODUKTFELD_TYPEN) */
  typ:
    | 'single_line_text_field'
    | 'multi_line_text_field'
    | 'number_integer'
    | 'number_decimal'
    | 'boolean'
    | 'list.single_line_text_field'
    | 'jahr';
  /** Einheit bei Zahlen, z. B. „mm“ */
  einheit?: string;
  auswahl?: string[];
  /** Schreibweisen aus Feeds und Shops → Wert der Auswahl (ohne Groß-/Kleinschreibung), z. B. „automatic“ → „Automatik“ */
  werte?: Record<string, string>;
  gruppe: string;
  pflicht?: boolean;
  /** Vorgabe: Händler dürfen das Feld ausfüllen (ohne Angabe: ja) */
  freigegeben?: boolean;
  /** Feldnamen in Feed, CSV oder Händler-Shop (DE/EN) für die Zuordnung */
  quellen: string[];
  /** Hinweis für die KI-Anreicherung */
  ki: string;
  beschreibung?: string;
}

export interface BranchenVorlage {
  branche: Branche;
  /** Gruppen in der Reihenfolge der Anzeige */
  gruppen: string[];
  felder: VorlageFeld[];
  /** Qualitätsregeln, die beim Setzen der Branche gelten */
  regeln: {
    eanPflicht: boolean;
    ausrichtung: string;
    /** Mindestlänge der Beschreibung, 0 = freiwillig */
    beschreibungMin: number;
    /** Marke erforderlich */
    markePflicht: boolean;
  };
  /** Buy Box: Pflichtmerkmale, wenn die Angebote nach Merkmalen zusammengefasst werden sollen (voreingestellt) */
  buyBoxMerkmale: BuyBoxMerkmal[] | null;
}

/** Gruppe für Felder ohne Gruppe (eigene Felder, Felder aus dem Shop) */
export const GRUPPE_WEITERE = 'Weitere Angaben';

/* ---------------------------------------------------------------- gemeinsame Bausteine */

const ZUSTAND_WERTE: Record<string, string> = {
  new: 'Neu', brandneu: 'Neu', 'brand new': 'Neu', neuwertig: 'Ungetragen', unworn: 'Ungetragen', 'like new': 'Ungetragen', ungetragen: 'Ungetragen',
  'very good': 'Sehr gut', excellent: 'Sehr gut', good: 'Gut', fair: 'Getragen', used: 'Getragen', worn: 'Getragen',
  'pre-owned': 'Getragen', gebraucht: 'Getragen', 'gut erhalten': 'Gut',
};

const zustand = (gruppe: string, auswahl = ['Neu', 'Ungetragen', 'Sehr gut', 'Gut', 'Getragen']): VorlageFeld => ({
  schluessel: 'zustand',
  name: 'Zustand',
  typ: 'single_line_text_field',
  auswahl,
  werte: ZUSTAND_WERTE,
  gruppe,
  pflicht: true,
  quellen: ['Zustand', 'Condition', 'Artikelzustand', 'Item condition', 'item_condition', 'condition'],
  ki: 'Zustand nur aus ausdrücklichen Angaben im Text übernehmen, nie raten. „Neu“ nur bei ungetragener Neuware mit voller Garantie.',
});

const zustandBeschreibung = (gruppe: string): VorlageFeld => ({
  schluessel: 'zustand_beschreibung',
  name: 'Zustandsbeschreibung',
  typ: 'multi_line_text_field',
  gruppe,
  quellen: ['Zustandsbeschreibung', 'Condition description', 'Condition notes', 'condition_description', 'Gebrauchsspuren'],
  ki: 'Gebrauchsspuren, Kratzer, Politur oder Revisionen sachlich in ein bis drei Sätzen beschreiben.',
});

const GESCHLECHT_WERTE: Record<string, string> = {
  men: 'Herren', male: 'Herren', mens: 'Herren', "men's": 'Herren', herr: 'Herren', man: 'Herren',
  women: 'Damen', female: 'Damen', ladies: 'Damen', womens: 'Damen', "women's": 'Damen', dame: 'Damen', woman: 'Damen',
  unisex: 'Unisex',
};

const geschlecht = (gruppe: string, auswahl = ['Herren', 'Damen', 'Unisex']): VorlageFeld => ({
  schluessel: 'geschlecht',
  name: 'Geschlecht',
  typ: 'single_line_text_field',
  auswahl,
  werte: { ...GESCHLECHT_WERTE, girls: 'Mädchen', boys: 'Jungen', kids: 'Unisex' },
  gruppe,
  quellen: ['Geschlecht', 'Gender', 'Zielgruppe', 'Target gender', 'gender', 'Abteilung', 'Department'],
  ki: 'Nur angeben, wenn Hersteller oder Händler das Produkt ausdrücklich für Herren, Damen oder beide ausweisen.',
});

const FARBEN = ['Schwarz', 'Weiß', 'Silber', 'Grau', 'Blau', 'Grün', 'Braun', 'Beige', 'Champagner', 'Gold', 'Rot', 'Rosa', 'Lila', 'Gelb', 'Orange', 'Perlmutt', 'Mehrfarbig'];
const FARB_WERTE: Record<string, string> = {
  black: 'Schwarz', white: 'Weiß', weiss: 'Weiß', silver: 'Silber', grey: 'Grau', gray: 'Grau', anthrazit: 'Grau', anthracite: 'Grau',
  blue: 'Blau', navy: 'Blau', dunkelblau: 'Blau', green: 'Grün', gruen: 'Grün', brown: 'Braun', beige: 'Beige', cream: 'Beige', creme: 'Beige',
  champagne: 'Champagner', gold: 'Gold', golden: 'Gold', red: 'Rot', pink: 'Rosa', rose: 'Rosa', purple: 'Lila', violet: 'Lila', violett: 'Lila',
  yellow: 'Gelb', orange: 'Orange', 'mother of pearl': 'Perlmutt', 'mother-of-pearl': 'Perlmutt', mop: 'Perlmutt', multicolor: 'Mehrfarbig', bunt: 'Mehrfarbig',
};

const METALL_WERTE: Record<string, string> = {
  steel: 'Edelstahl', 'stainless steel': 'Edelstahl', stahl: 'Edelstahl', 'yellow gold': 'Gelbgold', gold: 'Gelbgold', 'rose gold': 'Roségold',
  rosegold: 'Roségold', 'red gold': 'Roségold', rotgold: 'Roségold', 'pink gold': 'Roségold', 'everose gold': 'Roségold', 'white gold': 'Weißgold',
  weissgold: 'Weißgold', platinum: 'Platin', titanium: 'Titan', ceramic: 'Keramik', silver: 'Silber', 'sterling silver': 'Silber', bronze: 'Bronze',
  carbon: 'Carbon', palladium: 'Palladium', plastic: 'Kunststoff', resin: 'Kunststoff',
  'steel/gold': 'Edelstahl/Gold', 'steel and gold': 'Edelstahl/Gold', bicolor: 'Edelstahl/Gold', 'two-tone': 'Edelstahl/Gold', 'stahl/gold': 'Edelstahl/Gold', rolesor: 'Edelstahl/Gold',
};

/* ---------------------------------------------------------------- Bausteine für alle Branchen */

/** Gruppe der Pflichtangaben zur Produktsicherheit (GPSR, Verordnung (EU) 2023/988) */
export const GRUPPE_SICHERHEIT = 'Produktsicherheit';

/**
 * GPSR: Online-Angebote nennen Hersteller (Name, Post- und elektronische Anschrift), bei Herstellern außerhalb der EU
 * die verantwortliche Person in der EU, sowie Warn- und Sicherheitshinweise. Pflicht nur in Branchen mit überwiegend
 * Neuware – Antiquitäten sind ausgenommen, bei Gebrauchtware entscheidet der Marktplatz.
 */
const gpsr = (o: { pflicht?: boolean } = {}): VorlageFeld[] => [
  {
    schluessel: 'gpsr_hersteller',
    name: 'Hersteller (Name und Anschrift)',
    typ: 'multi_line_text_field',
    gruppe: GRUPPE_SICHERHEIT,
    pflicht: !!o.pflicht,
    quellen: ['Hersteller', 'Herstellerangaben', 'Herstelleranschrift', 'Manufacturer', 'Manufacturer address', 'manufacturer_info', 'GPSR Hersteller', 'gpsr_manufacturer'],
    ki: 'Name, Postanschrift und E-Mail oder Website des Herstellers nur aus den Daten übernehmen – nie erfinden oder aus der Marke ableiten.',
    beschreibung: 'Pflichtangabe nach GPSR: Name, Postanschrift und elektronische Adresse des Herstellers.',
  },
  {
    schluessel: 'gpsr_verantwortlicher',
    name: 'Verantwortliche Person in der EU',
    typ: 'multi_line_text_field',
    gruppe: GRUPPE_SICHERHEIT,
    quellen: ['Verantwortliche Person', 'EU-Verantwortlicher', 'Responsible person', 'EU responsible person', 'Importeur', 'Importer', 'gpsr_responsible_person'],
    ki: 'Nur bei Herstellern außerhalb der EU: Name und Anschrift der verantwortlichen Person bzw. des Importeurs aus den Daten übernehmen, nie erfinden.',
    beschreibung: 'Bei Herstellern außerhalb der EU: Name, Postanschrift und elektronische Adresse der verantwortlichen Person in der EU.',
  },
  {
    schluessel: 'sicherheitshinweise',
    name: 'Warn- und Sicherheitshinweise',
    typ: 'multi_line_text_field',
    gruppe: GRUPPE_SICHERHEIT,
    quellen: ['Sicherheitshinweise', 'Warnhinweise', 'Safety information', 'Safety warnings', 'Warnings', 'safety_information', 'GPSR Sicherheitshinweise'],
    ki: 'Warn- und Sicherheitshinweise wörtlich aus Etikett oder Herstellerangaben übernehmen; nichts ergänzen.',
  },
];

/** Grundpreis (PAngV): Nettofüllmenge und Bezugseinheit – den Grundpreis selbst ergibt der Preis je Variante */
const grundpreis = (gruppe: string, pflicht = true): VorlageFeld[] => [
  {
    schluessel: 'nettofuellmenge',
    name: 'Nettofüllmenge',
    typ: 'single_line_text_field',
    gruppe,
    pflicht,
    quellen: ['Nettofüllmenge', 'Füllmenge', 'Inhalt', 'Nenninhalt', 'Menge', 'Net content', 'Net quantity', 'Content', 'unit_pricing_measure'],
    ki: 'Menge mit Einheit wie auf der Verpackung, z. B. „500 g“, „0,75 l“, „60 Kapseln“; bei Mehrstückpackungen „6 × 0,5 l“.',
  },
  {
    schluessel: 'grundpreis_einheit',
    name: 'Grundpreis je',
    typ: 'single_line_text_field',
    auswahl: ['1 kg', '1 l', '1 m', '1 m²', '1 m³', '1 Stück', '1 Waschgang'],
    werte: { kg: '1 kg', '1kg': '1 kg', '100 g': '1 kg', '100g': '1 kg', l: '1 l', '1l': '1 l', '100 ml': '1 l', '100ml': '1 l', liter: '1 l', m: '1 m', m2: '1 m²', 'm²': '1 m²', m3: '1 m³', stk: '1 Stück', 'stück': '1 Stück', ct: '1 Stück', wl: '1 Waschgang', waschgang: '1 Waschgang' },
    gruppe,
    pflicht,
    quellen: ['Grundpreis je', 'Grundpreiseinheit', 'Bezugsmenge', 'unit_pricing_base_measure', 'Base unit', 'Unit price base'],
    ki: 'Bezugseinheit für den Grundpreis nach Preisangabenverordnung: 1 kg bzw. 1 l bei Gewicht oder Volumen, 1 Waschgang bei Waschmittel.',
  },
];

const ZUSTAND_WARE_WERTE: Record<string, string> = {
  new: 'Neu', neu: 'Neu', 'brand new': 'Neu', 'like new': 'Neuwertig', 'open box': 'Neuwertig', 'b-ware': 'Neuwertig', neuwertig: 'Neuwertig',
  refurbished: 'Generalüberholt', renewed: 'Generalüberholt', generalueberholt: 'Generalüberholt', 'very good': 'Gebraucht – sehr gut',
  good: 'Gebraucht – gut', acceptable: 'Gebraucht – akzeptabel', fair: 'Gebraucht – akzeptabel', used: 'Gebraucht – gut', gebraucht: 'Gebraucht – gut',
  'pre-owned': 'Gebraucht – gut', defekt: 'Defekt / für Bastler', 'for parts': 'Defekt / für Bastler',
};

/** Zustand für Ware, die meist neu, aber auch gebraucht oder generalüberholt verkauft wird */
const zustandWare = (gruppe: string, o: { pflicht?: boolean; generalueberholt?: boolean; defekt?: boolean } = {}): VorlageFeld => ({
  schluessel: 'zustand',
  name: 'Zustand',
  typ: 'single_line_text_field',
  auswahl: ['Neu', 'Neuwertig', ...(o.generalueberholt ? ['Generalüberholt'] : []), 'Gebraucht – sehr gut', 'Gebraucht – gut', 'Gebraucht – akzeptabel', ...(o.defekt ? ['Defekt / für Bastler'] : [])],
  werte: ZUSTAND_WARE_WERTE,
  gruppe,
  pflicht: !!o.pflicht,
  quellen: ['Zustand', 'Condition', 'Artikelzustand', 'Item condition', 'item_condition', 'condition'],
  ki: 'Zustand nur aus ausdrücklichen Angaben übernehmen, nie raten. Ohne Hinweis auf Gebrauch ist Ware eines Händlers „Neu“.',
});

const modell = (gruppe: string, o: { pflicht?: boolean; ki?: string } = {}): VorlageFeld => ({
  schluessel: 'modell',
  name: 'Modell',
  typ: 'single_line_text_field',
  gruppe,
  pflicht: !!o.pflicht,
  quellen: ['Modell', 'Model', 'Modellname', 'Model name', 'model_name', 'Produktlinie'],
  ki: o.ki ?? 'Modellname des Herstellers ohne Marke, Farbe und Größe.',
});

const mpn = (gruppe: string, pflicht = false): VorlageFeld => ({
  schluessel: 'mpn',
  name: 'Hersteller-Teilenummer (MPN)',
  typ: 'single_line_text_field',
  gruppe,
  pflicht,
  quellen: ['MPN', 'Hersteller-Teilenummer', 'Herstellernummer', 'Hersteller-Artikelnummer', 'Manufacturer part number', 'Part number', 'mpn', 'Artikelnummer Hersteller'],
  ki: 'Artikelnummer des Herstellers exakt übernehmen (z. B. „MU7A3ZD/A“); keine SKU des Händlers, keine EAN.',
});

const FARBEN_WARE = [...FARBEN.filter((f) => !['Champagner', 'Perlmutt'].includes(f)), 'Transparent'];

const farbe = (gruppe: string, o: { pflicht?: boolean; ki?: string } = {}): VorlageFeld => ({
  schluessel: 'farbe',
  name: 'Farbe',
  typ: 'single_line_text_field',
  auswahl: FARBEN_WARE,
  werte: { ...FARB_WERTE, transparent: 'Transparent', clear: 'Transparent', klar: 'Transparent', natur: 'Beige', natural: 'Beige' },
  gruppe,
  pflicht: !!o.pflicht,
  quellen: ['Farbe', 'Color', 'Colour', 'Hauptfarbe', 'Main color', 'color', 'Farbton'],
  ki: o.ki ?? 'Hauptfarbe des Artikels.',
});

const material = (gruppe: string, o: { name?: string; ki?: string; pflicht?: boolean } = {}): VorlageFeld => ({
  schluessel: 'material',
  name: o.name ?? 'Material',
  typ: 'single_line_text_field',
  gruppe,
  pflicht: !!o.pflicht,
  quellen: ['Material', 'Materialien', 'Zusammensetzung', 'Werkstoff', 'Material composition', 'Composition', 'material'],
  ki: o.ki ?? 'Hauptmaterial(ien), z. B. „Eiche massiv“ oder „Aluminium, Kunststoff“.',
});

const masse = (gruppe: string): VorlageFeld[] => [
  { schluessel: 'breite', name: 'Breite', typ: 'number_decimal', einheit: 'cm', gruppe, quellen: ['Breite', 'Width', 'width', 'B'], ki: 'Breite in Zentimetern, nur die Zahl.' },
  { schluessel: 'hoehe', name: 'Höhe', typ: 'number_decimal', einheit: 'cm', gruppe, quellen: ['Höhe', 'Hoehe', 'Height', 'height', 'H'], ki: 'Höhe in Zentimetern, nur die Zahl.' },
  { schluessel: 'tiefe', name: 'Tiefe', typ: 'number_decimal', einheit: 'cm', gruppe, quellen: ['Tiefe', 'Depth', 'depth', 'Länge', 'Length', 'T'], ki: 'Tiefe bzw. Länge in Zentimetern, nur die Zahl.' },
  { schluessel: 'produktgewicht', name: 'Gewicht', typ: 'number_decimal', einheit: 'kg', gruppe, quellen: ['Gewicht', 'Produktgewicht', 'Nettogewicht', 'Weight', 'Item weight', 'weight'], ki: 'Gewicht des Produkts ohne Verpackung in Kilogramm.' },
];

const herkunftsland = (gruppe: string, o: { pflicht?: boolean; ki?: string } = {}): VorlageFeld => ({
  schluessel: 'herkunftsland',
  name: 'Herkunftsland',
  typ: 'single_line_text_field',
  gruppe,
  pflicht: !!o.pflicht,
  quellen: ['Herkunftsland', 'Ursprungsland', 'Made in', 'Country of origin', 'Origin', 'country_of_origin'],
  ki: o.ki ?? 'Herkunfts- bzw. Ursprungsland nur aus ausdrücklichen Angaben, z. B. „Italien“.',
});

const altersempfehlung = (gruppe: string, pflicht = false): VorlageFeld => ({
  schluessel: 'altersempfehlung',
  name: 'Altersempfehlung',
  typ: 'single_line_text_field',
  gruppe,
  pflicht,
  quellen: ['Altersempfehlung', 'Alter', 'Empfohlenes Alter', 'Age', 'Age range', 'Recommended age', 'age_group', 'Ab Jahren'],
  ki: 'Altersangabe des Herstellers, z. B. „ab 3 Jahren“, „0–12 Monate“ oder „ab 14 Jahren“.',
});

const anwendung = (gruppe: string, o: { name?: string; ki?: string; pflicht?: boolean } = {}): VorlageFeld => ({
  schluessel: 'anwendung',
  name: o.name ?? 'Anwendung',
  typ: 'multi_line_text_field',
  gruppe,
  pflicht: !!o.pflicht,
  quellen: ['Anwendung', 'Anwendungshinweise', 'Gebrauchsanweisung', 'Directions', 'How to use', 'Usage', 'Directions for use'],
  ki: o.ki ?? 'Anwendung laut Hersteller in ein bis drei Sätzen.',
});

/** Elektro- und Elektronikgeräte: Energielabel (EU 2017/1369, EPREL), WEEE (ElektroG) */
const energie = (gruppe: string): VorlageFeld[] => [
  {
    schluessel: 'energieeffizienzklasse',
    name: 'Energieeffizienzklasse',
    typ: 'single_line_text_field',
    auswahl: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'A+++', 'A++', 'A+'],
    werte: { 'klasse a': 'A', 'class a': 'A', 'klasse b': 'B', 'class b': 'B', 'klasse c': 'C', 'class c': 'C', 'klasse d': 'D', 'class d': 'D', 'klasse e': 'E', 'klasse f': 'F', 'klasse g': 'G' },
    gruppe,
    quellen: ['Energieeffizienzklasse', 'Energieklasse', 'Energy efficiency class', 'Energy class', 'energy_efficiency_class', 'EEK'],
    ki: 'Energieeffizienzklasse laut Energielabel; nur bei kennzeichnungspflichtigen Geräten und nur aus Herstellerangaben.',
  },
  {
    schluessel: 'energielabel',
    name: 'Energielabel (Link)',
    typ: 'single_line_text_field',
    gruppe,
    quellen: ['Energielabel', 'Energy label', 'energy_label_url', 'Label-URL'],
    ki: 'Link zum Energielabel (z. B. aus der EPREL-Datenbank) nur aus den Daten übernehmen.',
    beschreibung: 'Bei kennzeichnungspflichtigen Geräten muss das Label beim Preis abrufbar sein.',
  },
  {
    schluessel: 'produktdatenblatt',
    name: 'Produktdatenblatt (Link)',
    typ: 'single_line_text_field',
    gruppe,
    quellen: ['Produktdatenblatt', 'Datenblatt', 'Product information sheet', 'Product fiche', 'product_fiche_url', 'EPREL'],
    ki: 'Link zum Produktdatenblatt (EPREL) nur aus den Daten übernehmen.',
  },
  {
    schluessel: 'weee_nummer',
    name: 'WEEE-Reg.-Nr.',
    typ: 'single_line_text_field',
    gruppe,
    quellen: ['WEEE-Reg.-Nr.', 'WEEE', 'WEEE-Nummer', 'WEEE registration number', 'weee_number', 'ElektroG'],
    ki: 'Registrierungsnummer nach ElektroG (z. B. „DE 12345678“) nur aus den Daten übernehmen.',
  },
];

/** Batterien und Akkus (BattDG, Batterieverordnung (EU) 2023/1542) – auch für Versand und Rücknahme */
const batterie = (gruppe: string): VorlageFeld[] => [
  {
    schluessel: 'batterie_enthalten',
    name: 'Batterie bzw. Akku enthalten',
    typ: 'boolean',
    gruppe,
    quellen: ['Batterie enthalten', 'Akku enthalten', 'Batteries included', 'Contains battery', 'battery_included'],
    ki: 'Ja, wenn eine Batterie oder ein Akku beiliegt oder fest eingebaut ist.',
  },
  {
    schluessel: 'batterie_typ',
    name: 'Batterie- bzw. Akkutyp',
    typ: 'single_line_text_field',
    auswahl: ['Lithium-Ionen', 'Lithium-Polymer', 'Lithium-Metall', 'Alkali', 'Nickel-Metallhydrid', 'Blei', 'Sonstige'],
    werte: { 'li-ion': 'Lithium-Ionen', 'lithium ion': 'Lithium-Ionen', 'lithium-ion': 'Lithium-Ionen', lipo: 'Lithium-Polymer', 'li-po': 'Lithium-Polymer', lithium: 'Lithium-Metall', alkaline: 'Alkali', nimh: 'Nickel-Metallhydrid', 'ni-mh': 'Nickel-Metallhydrid', lead: 'Blei', 'lead acid': 'Blei' },
    gruppe,
    quellen: ['Batterietyp', 'Akkutyp', 'Battery type', 'Battery chemistry', 'battery_type'],
    ki: 'Chemie der Batterie bzw. des Akkus (wichtig für Versand und Rücknahme).',
  },
  {
    schluessel: 'akku_kapazitaet',
    name: 'Akkukapazität',
    typ: 'number_integer',
    einheit: 'mAh',
    gruppe,
    quellen: ['Akkukapazität', 'Kapazität', 'Battery capacity', 'Capacity', 'mAh'],
    ki: 'Kapazität in mAh, nur die Zahl.',
  },
];

const ALLERGENE = ['Glutenhaltiges Getreide', 'Krebstiere', 'Eier', 'Fisch', 'Erdnüsse', 'Soja', 'Milch', 'Schalenfrüchte', 'Sellerie', 'Senf', 'Sesam', 'Schwefeldioxid/Sulfite', 'Lupinen', 'Weichtiere', 'Keine'];
const ALLERGEN_WERTE: Record<string, string> = {
  gluten: 'Glutenhaltiges Getreide', weizen: 'Glutenhaltiges Getreide', wheat: 'Glutenhaltiges Getreide', crustaceans: 'Krebstiere', eggs: 'Eier', ei: 'Eier', egg: 'Eier',
  fish: 'Fisch', peanuts: 'Erdnüsse', erdnuss: 'Erdnüsse', soy: 'Soja', soya: 'Soja', milk: 'Milch', laktose: 'Milch', lactose: 'Milch', nuts: 'Schalenfrüchte',
  'nüsse': 'Schalenfrüchte', 'tree nuts': 'Schalenfrüchte', celery: 'Sellerie', mustard: 'Senf', sesame: 'Sesam', sulphites: 'Schwefeldioxid/Sulfite',
  sulfite: 'Schwefeldioxid/Sulfite', sulfites: 'Schwefeldioxid/Sulfite', lupin: 'Lupinen', molluscs: 'Weichtiere', none: 'Keine',
};

/** LMIV Anhang II: 14 Hauptallergene */
const allergene = (gruppe: string, pflicht = true): VorlageFeld => ({
  schluessel: 'allergene',
  name: 'Allergene',
  typ: 'list.single_line_text_field',
  auswahl: ALLERGENE,
  werte: ALLERGEN_WERTE,
  gruppe,
  pflicht,
  quellen: ['Allergene', 'Allergenkennzeichnung', 'Allergens', 'Contains', 'Enthält', 'allergens'],
  ki: 'Nur die 14 Hauptallergene nach LMIV, die laut Zutatenliste enthalten sind; „Keine“ nur bei ausdrücklicher Angabe. Nie raten.',
});

const zutaten = (gruppe: string, pflicht = true): VorlageFeld => ({
  schluessel: 'zutaten',
  name: 'Zutatenverzeichnis',
  typ: 'multi_line_text_field',
  gruppe,
  pflicht,
  quellen: ['Zutaten', 'Zutatenliste', 'Zutatenverzeichnis', 'Ingredients', 'ingredients'],
  ki: 'Zutatenliste wörtlich von der Verpackung übernehmen, Allergene hervorgehoben wie im Original. Nie ergänzen oder erfinden.',
});

const NAEHRWERTE: { schluessel: string; name: string; einheit: string; quellen: string[] }[] = [
  { schluessel: 'brennwert_kj', name: 'Brennwert (kJ)', einheit: 'kJ', quellen: ['Brennwert kJ', 'Energie kJ', 'Energy kJ', 'energy_kj'] },
  { schluessel: 'brennwert_kcal', name: 'Brennwert (kcal)', einheit: 'kcal', quellen: ['Brennwert kcal', 'Kalorien', 'Energy kcal', 'Calories', 'energy_kcal'] },
  { schluessel: 'fett', name: 'Fett', einheit: 'g', quellen: ['Fett', 'Fat', 'Total fat'] },
  { schluessel: 'gesaettigte_fettsaeuren', name: 'davon gesättigte Fettsäuren', einheit: 'g', quellen: ['Gesättigte Fettsäuren', 'davon gesättigte Fettsäuren', 'Saturated fat', 'Saturates'] },
  { schluessel: 'kohlenhydrate', name: 'Kohlenhydrate', einheit: 'g', quellen: ['Kohlenhydrate', 'Carbohydrates', 'Carbohydrate'] },
  { schluessel: 'zucker', name: 'davon Zucker', einheit: 'g', quellen: ['Zucker', 'davon Zucker', 'Sugars', 'Sugar'] },
  { schluessel: 'eiweiss', name: 'Eiweiß', einheit: 'g', quellen: ['Eiweiß', 'Eiweiss', 'Protein'] },
  { schluessel: 'salz', name: 'Salz', einheit: 'g', quellen: ['Salz', 'Salt'] },
];

/** LMIV Art. 30: Nährwertdeklaration je 100 g bzw. 100 ml (Brennwert in kJ und kcal plus sieben Nährstoffe) */
const naehrwerte = (gruppe: string, pflicht = true): VorlageFeld[] => [
  {
    schluessel: 'naehrwerte_bezug',
    name: 'Nährwerte je',
    typ: 'single_line_text_field',
    auswahl: ['100 g', '100 ml'],
    werte: { '100g': '100 g', '100ml': '100 ml', g: '100 g', ml: '100 ml' },
    gruppe,
    pflicht,
    quellen: ['Nährwerte je', 'Bezugsgröße', 'Nutrition per', 'Per 100'],
    ki: 'Bezugsgröße der Nährwerttabelle: 100 g bei festen, 100 ml bei flüssigen Lebensmitteln.',
  },
  ...NAEHRWERTE.map((n): VorlageFeld => ({
    schluessel: n.schluessel,
    name: n.name,
    typ: 'number_decimal',
    einheit: n.einheit,
    gruppe,
    pflicht,
    quellen: n.quellen,
    ki: `Wert je 100 g bzw. 100 ml laut Nährwerttabelle, nur die Zahl in ${n.einheit}; nie schätzen.`,
  })),
];

/* ---------------------------------------------------------------- Uhren */

const UHREN: BranchenVorlage = {
  branche: 'uhren',
  gruppen: ['Zustand & Lieferumfang', 'Produktdetails', 'Material & Farbe', 'Funktionen', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: false, ausrichtung: 'Luxusuhren, neu und gebraucht', beschreibungMin: 0, markePflicht: true },
  buyBoxMerkmale: ['marke', 'modell', 'farbe', 'groesse', 'zustand'],
  felder: [
    zustand('Zustand & Lieferumfang'),
    zustandBeschreibung('Zustand & Lieferumfang'),
    {
      schluessel: 'dokumente',
      name: 'Dokumente',
      typ: 'list.single_line_text_field',
      auswahl: ['Originalpapiere', 'Zertifikat', 'Garantiekarte', 'Kaufbeleg', 'Servicenachweis', 'Keine'],
      werte: { papers: 'Originalpapiere', 'original papers': 'Originalpapiere', papiere: 'Originalpapiere', certificate: 'Zertifikat', 'warranty card': 'Garantiekarte', garantie: 'Garantiekarte', receipt: 'Kaufbeleg', rechnung: 'Kaufbeleg', 'service papers': 'Servicenachweis', none: 'Keine', 'no papers': 'Keine', ohne: 'Keine' },
      gruppe: 'Zustand & Lieferumfang',
      quellen: ['Dokumente', 'Papiere', 'Papers', 'Documents', 'Box & Papiere', 'box_papers', 'Lieferumfang', 'Scope of delivery'],
      ki: 'Mehrere Werte möglich. Nur übernehmen, was ausdrücklich zum Lieferumfang gehört; „Keine“ nur bei ausdrücklich fehlenden Papieren.',
    },
    {
      schluessel: 'verpackung',
      name: 'Verpackung',
      typ: 'single_line_text_field',
      auswahl: ['Original-Box', 'Ersatzbox', 'Ohne Box'],
      werte: { 'original box': 'Original-Box', box: 'Original-Box', originalbox: 'Original-Box', 'replacement box': 'Ersatzbox', 'no box': 'Ohne Box', ohne: 'Ohne Box', none: 'Ohne Box' },
      gruppe: 'Zustand & Lieferumfang',
      quellen: ['Verpackung', 'Box', 'Packaging', 'Original box', 'Uhrenbox'],
      ki: 'Original-Box nur, wenn die Herstellerbox ausdrücklich dabei ist.',
    },
    {
      schluessel: 'garantie',
      name: 'Garantie',
      typ: 'number_integer',
      einheit: 'Monate',
      gruppe: 'Zustand & Lieferumfang',
      quellen: ['Garantie', 'Warranty', 'Gewährleistung', 'Garantie (Monate)', 'warranty_months'],
      ki: 'Garantie des Händlers oder Herstellers in Monaten; Jahre umrechnen (2 Jahre = 24).',
    },
    {
      schluessel: 'modell',
      name: 'Modell',
      typ: 'single_line_text_field',
      gruppe: 'Produktdetails',
      pflicht: true,
      quellen: ['Modell', 'Model', 'Modellname', 'Model name', 'model_name', 'Uhrenmodell'],
      ki: 'Modellname ohne Marke, z. B. „Submariner Date“ oder „Speedmaster Professional“.',
    },
    {
      schluessel: 'serie',
      name: 'Serie / Kollektion',
      typ: 'single_line_text_field',
      gruppe: 'Produktdetails',
      quellen: ['Serie', 'Kollektion', 'Collection', 'Series', 'Modellreihe', 'Model family', 'Familie'],
      ki: 'Modellfamilie des Herstellers, z. B. „Oyster Perpetual“, „Seamaster“, „Royal Oak“.',
    },
    {
      schluessel: 'referenz',
      name: 'Referenz',
      typ: 'single_line_text_field',
      gruppe: 'Produktdetails',
      pflicht: true,
      quellen: ['Referenz', 'Referenznummer', 'Reference', 'Reference number', 'Ref.', 'Ref', 'reference_number', 'Modellnummer', 'Model number', 'MPN'],
      ki: 'Referenznummer des Herstellers exakt übernehmen (z. B. „126610LN“), keine Seriennummer.',
    },
    {
      schluessel: 'werk',
      name: 'Werk',
      typ: 'single_line_text_field',
      auswahl: ['Automatik', 'Handaufzug', 'Quarz', 'Solar'],
      werte: { automatic: 'Automatik', 'self-winding': 'Automatik', automatisch: 'Automatik', selbstaufzug: 'Automatik', manual: 'Handaufzug', 'manual winding': 'Handaufzug', 'hand-wound': 'Handaufzug', handaufzug: 'Handaufzug', quartz: 'Quarz', quarz: 'Quarz', batterie: 'Quarz', solar: 'Solar', 'eco-drive': 'Solar' },
      gruppe: 'Produktdetails',
      quellen: ['Werk', 'Uhrwerk', 'Aufzug', 'Movement', 'Movement type', 'Antrieb', 'movement'],
      ki: 'Art des Aufzugs: Automatik, Handaufzug, Quarz oder Solar.',
    },
    {
      schluessel: 'kaliber',
      name: 'Kaliber',
      typ: 'single_line_text_field',
      gruppe: 'Produktdetails',
      quellen: ['Kaliber', 'Caliber', 'Calibre', 'Werkkaliber', 'Movement caliber'],
      ki: 'Kaliberbezeichnung des Werks, z. B. „3235“ oder „ETA 2824-2“.',
    },
    {
      schluessel: 'gangreserve',
      name: 'Gangreserve',
      typ: 'number_integer',
      einheit: 'h',
      gruppe: 'Produktdetails',
      quellen: ['Gangreserve', 'Power reserve', 'Laufzeit'],
      ki: 'Gangreserve in Stunden; Tage umrechnen (3 Tage = 72).',
    },
    {
      schluessel: 'gehaeusedurchmesser',
      name: 'Gehäusedurchmesser',
      typ: 'number_decimal',
      einheit: 'mm',
      gruppe: 'Produktdetails',
      quellen: ['Gehäusedurchmesser', 'Gehäusegröße', 'Durchmesser', 'Case diameter', 'Case size', 'case_size', 'case_diameter', 'Größe (mm)'],
      ki: 'Durchmesser ohne Krone in Millimetern, nur die Zahl (z. B. 41).',
    },
    {
      schluessel: 'gehaeusehoehe',
      name: 'Gehäusehöhe',
      typ: 'number_decimal',
      einheit: 'mm',
      gruppe: 'Produktdetails',
      quellen: ['Gehäusehöhe', 'Höhe', 'Case thickness', 'Thickness', 'Bauhöhe'],
      ki: 'Gehäusehöhe in Millimetern, nur die Zahl.',
    },
    {
      schluessel: 'baujahr',
      name: 'Baujahr',
      typ: 'jahr',
      gruppe: 'Produktdetails',
      quellen: ['Baujahr', 'Jahr', 'Year', 'Year of production', 'production_year', 'Herstellungsjahr', 'Kaufjahr'],
      ki: 'Vierstelliges Jahr; nur angeben, wenn es aus Papieren oder Text eindeutig hervorgeht.',
    },
    geschlecht('Produktdetails'),
    {
      schluessel: 'wasserdichtigkeit',
      name: 'Wasserdichtigkeit',
      typ: 'number_integer',
      einheit: 'm',
      gruppe: 'Produktdetails',
      quellen: ['Wasserdichtigkeit', 'Wasserdicht', 'Water resistance', 'Water resistant', 'water_resistance', 'Wasserdichtheit'],
      ki: 'Wasserdichtigkeit in Metern; bar und ATM umrechnen (10 bar = 100 m).',
    },
    {
      schluessel: 'gehaeusematerial',
      name: 'Gehäusematerial',
      typ: 'single_line_text_field',
      auswahl: ['Edelstahl', 'Gelbgold', 'Roségold', 'Weißgold', 'Platin', 'Titan', 'Keramik', 'Carbon', 'Bronze', 'Silber', 'Edelstahl/Gold', 'Kunststoff'],
      werte: METALL_WERTE,
      gruppe: 'Material & Farbe',
      quellen: ['Gehäusematerial', 'Gehäuse', 'Case material', 'case_material', 'Material Gehäuse'],
      ki: 'Hauptmaterial des Gehäuses; Bicolor als „Edelstahl/Gold“.',
    },
    {
      schluessel: 'armbandmaterial',
      name: 'Armbandmaterial',
      typ: 'single_line_text_field',
      auswahl: ['Edelstahl', 'Leder', 'Krokodilleder', 'Kautschuk', 'Textil', 'Gelbgold', 'Roségold', 'Weißgold', 'Platin', 'Titan', 'Keramik', 'Edelstahl/Gold', 'Sonstiges'],
      werte: { ...METALL_WERTE, leather: 'Leder', alligator: 'Krokodilleder', crocodile: 'Krokodilleder', rubber: 'Kautschuk', kautschuk: 'Kautschuk', silikon: 'Kautschuk', silicone: 'Kautschuk', textile: 'Textil', nato: 'Textil', fabric: 'Textil', nylon: 'Textil' },
      gruppe: 'Material & Farbe',
      quellen: ['Armbandmaterial', 'Armband', 'Bracelet material', 'Strap material', 'Band', 'bracelet_material'],
      ki: 'Material von Armband oder Band.',
    },
    {
      schluessel: 'schliesse',
      name: 'Schließe',
      typ: 'single_line_text_field',
      auswahl: ['Faltschließe', 'Dornschließe', 'Butterfly-Schließe', 'Sonstige'],
      werte: { 'folding clasp': 'Faltschließe', deployant: 'Faltschließe', 'deployment clasp': 'Faltschließe', faltschliesse: 'Faltschließe', buckle: 'Dornschließe', 'tang buckle': 'Dornschließe', 'pin buckle': 'Dornschließe', dornschliesse: 'Dornschließe', butterfly: 'Butterfly-Schließe' },
      gruppe: 'Material & Farbe',
      quellen: ['Schließe', 'Schliesse', 'Clasp', 'Buckle', 'Verschluss', 'clasp_type'],
      ki: 'Art der Schließe.',
    },
    {
      schluessel: 'uhrglas',
      name: 'Uhrglas',
      typ: 'single_line_text_field',
      auswahl: ['Saphirglas', 'Mineralglas', 'Acrylglas'],
      werte: { sapphire: 'Saphirglas', 'sapphire crystal': 'Saphirglas', saphir: 'Saphirglas', mineral: 'Mineralglas', 'mineral glass': 'Mineralglas', acrylic: 'Acrylglas', plexiglas: 'Acrylglas', plexiglass: 'Acrylglas', hesalite: 'Acrylglas', kunststoffglas: 'Acrylglas' },
      gruppe: 'Material & Farbe',
      quellen: ['Uhrglas', 'Glas', 'Crystal', 'Glass', 'crystal'],
      ki: 'Art des Uhrglases.',
    },
    {
      schluessel: 'zifferblattfarbe',
      name: 'Zifferblattfarbe',
      typ: 'single_line_text_field',
      auswahl: FARBEN,
      werte: FARB_WERTE,
      gruppe: 'Material & Farbe',
      quellen: ['Zifferblattfarbe', 'Zifferblatt', 'Dial color', 'Dial colour', 'Dial', 'dial_color', 'Farbe'],
      ki: 'Grundfarbe des Zifferblatts.',
    },
    {
      schluessel: 'indizes',
      name: 'Indizes / Ziffern',
      typ: 'single_line_text_field',
      auswahl: ['Strichindizes', 'Arabische Ziffern', 'Römische Ziffern', 'Diamanten', 'Ohne'],
      werte: { index: 'Strichindizes', indices: 'Strichindizes', baton: 'Strichindizes', stick: 'Strichindizes', arabic: 'Arabische Ziffern', 'arabic numerals': 'Arabische Ziffern', roman: 'Römische Ziffern', 'roman numerals': 'Römische Ziffern', diamonds: 'Diamanten', diamond: 'Diamanten', none: 'Ohne' },
      gruppe: 'Material & Farbe',
      quellen: ['Indizes', 'Ziffern', 'Zifferblatt-Ziffern', 'Numerals', 'Dial numerals', 'Hour markers'],
      ki: 'Art der Stundenmarkierungen.',
    },
    {
      schluessel: 'luenette',
      name: 'Lünette',
      typ: 'single_line_text_field',
      gruppe: 'Material & Farbe',
      quellen: ['Lünette', 'Luenette', 'Bezel', 'Bezel material', 'bezel'],
      ki: 'Art und Material der Lünette, z. B. „Drehbar, Keramik“ oder „Geriffelt, Weißgold“.',
    },
    {
      schluessel: 'funktionen',
      name: 'Funktionen',
      typ: 'list.single_line_text_field',
      auswahl: ['Datum', 'Tag-Datum', 'Chronograph', 'GMT', 'Weltzeit', 'Mondphase', 'Kleine Sekunde', 'Gangreserveanzeige', 'Wecker', 'Tourbillon', 'Ewiger Kalender', 'Jahreskalender', 'Minutenrepetition', 'Tachymeter', 'Flyback'],
      werte: { date: 'Datum', 'day-date': 'Tag-Datum', 'day date': 'Tag-Datum', chronograph: 'Chronograph', chrono: 'Chronograph', gmt: 'GMT', 'second time zone': 'GMT', 'world time': 'Weltzeit', worldtimer: 'Weltzeit', 'moon phase': 'Mondphase', moonphase: 'Mondphase', 'small seconds': 'Kleine Sekunde', 'power reserve indicator': 'Gangreserveanzeige', 'power reserve': 'Gangreserveanzeige', alarm: 'Wecker', tourbillon: 'Tourbillon', 'perpetual calendar': 'Ewiger Kalender', 'annual calendar': 'Jahreskalender', 'minute repeater': 'Minutenrepetition', tachymeter: 'Tachymeter', flyback: 'Flyback' },
      gruppe: 'Funktionen',
      quellen: ['Funktionen', 'Komplikationen', 'Functions', 'Complications', 'Features', 'functions'],
      ki: 'Alle Komplikationen der Uhr; mehrere Werte möglich.',
    },

    ...gpsr(),
  ],
};

/* ---------------------------------------------------------------- Schmuck */

const SCHMUCK: BranchenVorlage = {
  branche: 'schmuck',
  gruppen: ['Zustand & Lieferumfang', 'Produktdetails', 'Material', 'Edelsteine', 'Maße', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: false, ausrichtung: 'Schmuck und Edelsteine', beschreibungMin: 0, markePflicht: true },
  buyBoxMerkmale: null,
  felder: [
    zustand('Zustand & Lieferumfang'),
    zustandBeschreibung('Zustand & Lieferumfang'),
    {
      schluessel: 'zertifikat',
      name: 'Zertifikat',
      typ: 'list.single_line_text_field',
      auswahl: ['GIA', 'HRD', 'IGI', 'DPL', 'Echtheitszertifikat', 'Garantiekarte', 'Kaufbeleg', 'Keine'],
      werte: { 'certificate of authenticity': 'Echtheitszertifikat', certificate: 'Echtheitszertifikat', zertifikat: 'Echtheitszertifikat', 'warranty card': 'Garantiekarte', receipt: 'Kaufbeleg', none: 'Keine', ohne: 'Keine' },
      gruppe: 'Zustand & Lieferumfang',
      quellen: ['Zertifikat', 'Zertifikate', 'Certificate', 'Certification', 'Gutachten', 'Expertise', 'Lieferumfang'],
      ki: 'Mehrere Werte möglich; Labor (GIA, HRD, IGI, DPL) nur bei ausdrücklichem Gutachten.',
    },
    {
      schluessel: 'verpackung',
      name: 'Verpackung',
      typ: 'single_line_text_field',
      auswahl: ['Original-Etui', 'Ersatzetui', 'Ohne Etui'],
      werte: { 'original box': 'Original-Etui', box: 'Original-Etui', etui: 'Original-Etui', 'original case': 'Original-Etui', 'no box': 'Ohne Etui', none: 'Ohne Etui', ohne: 'Ohne Etui' },
      gruppe: 'Zustand & Lieferumfang',
      quellen: ['Verpackung', 'Etui', 'Box', 'Packaging'],
      ki: 'Original-Etui nur, wenn das Etui des Herstellers ausdrücklich dabei ist.',
    },
    {
      schluessel: 'schmuckart',
      name: 'Schmuckart',
      typ: 'single_line_text_field',
      auswahl: ['Ring', 'Kette', 'Collier', 'Anhänger', 'Armband', 'Armreif', 'Ohrringe', 'Ohrstecker', 'Creolen', 'Brosche', 'Manschettenknöpfe', 'Set'],
      werte: { ring: 'Ring', necklace: 'Kette', chain: 'Kette', collier: 'Collier', pendant: 'Anhänger', anhaenger: 'Anhänger', bracelet: 'Armband', bangle: 'Armreif', earrings: 'Ohrringe', 'stud earrings': 'Ohrstecker', studs: 'Ohrstecker', hoops: 'Creolen', 'hoop earrings': 'Creolen', brooch: 'Brosche', cufflinks: 'Manschettenknöpfe', set: 'Set' },
      gruppe: 'Produktdetails',
      pflicht: true,
      quellen: ['Schmuckart', 'Art', 'Typ', 'Jewelry type', 'Jewellery type', 'Type', 'Produktart', 'Product type'],
      ki: 'Art des Schmuckstücks.',
    },
    {
      schluessel: 'modell',
      name: 'Modell',
      typ: 'single_line_text_field',
      gruppe: 'Produktdetails',
      quellen: ['Modell', 'Model', 'Modellname', 'Referenz', 'Reference', 'Artikelname'],
      ki: 'Modellname oder Referenz des Herstellers ohne Marke, z. B. „Love Ring“.',
    },
    {
      schluessel: 'serie',
      name: 'Kollektion',
      typ: 'single_line_text_field',
      gruppe: 'Produktdetails',
      quellen: ['Kollektion', 'Serie', 'Collection', 'Series', 'Linie'],
      ki: 'Kollektion des Herstellers, z. B. „Juste un Clou“.',
    },
    geschlecht('Produktdetails', ['Damen', 'Herren', 'Unisex']),
    {
      schluessel: 'material',
      name: 'Material / Legierung',
      typ: 'single_line_text_field',
      auswahl: ['Gelbgold', 'Weißgold', 'Roségold', 'Bicolor-Gold', 'Tricolor-Gold', 'Platin', 'Palladium', 'Silber', 'Edelstahl', 'Titan', 'Sonstiges'],
      werte: { ...METALL_WERTE, bicolor: 'Bicolor-Gold', 'two-tone gold': 'Bicolor-Gold', tricolor: 'Tricolor-Gold', 'three-tone gold': 'Tricolor-Gold' },
      gruppe: 'Material',
      pflicht: true,
      quellen: ['Material', 'Legierung', 'Metall', 'Metal', 'Material type', 'Edelmetall'],
      ki: 'Metall bzw. Legierung ohne Feingehalt („750 Gelbgold“ → Material „Gelbgold“, Feingehalt „750“).',
    },
    {
      schluessel: 'feingehalt',
      name: 'Feingehalt',
      typ: 'single_line_text_field',
      auswahl: ['333', '375', '585', '750', '900', '916', '925', '950', '999'],
      werte: { '8k': '333', '8 kt': '333', '9k': '375', '9 kt': '375', '14k': '585', '14 kt': '585', '18k': '750', '18 kt': '750', '22k': '916', '22 kt': '916', sterling: '925', 'sterling silver': '925', '24k': '999' },
      gruppe: 'Material',
      quellen: ['Feingehalt', 'Punze', 'Fineness', 'Purity', 'Karat Gold', 'Gold purity', 'Legierung (‰)'],
      ki: 'Feingehalt in Promille (750 = 18 Karat); Karatangaben beim Gold umrechnen.',
    },
    {
      schluessel: 'gewicht',
      name: 'Gewicht',
      typ: 'number_decimal',
      einheit: 'g',
      gruppe: 'Material',
      quellen: ['Gewicht', 'Gesamtgewicht', 'Weight', 'Metal weight', 'Goldgewicht'],
      ki: 'Gesamtgewicht in Gramm, nur die Zahl.',
    },
    {
      schluessel: 'edelstein',
      name: 'Edelstein',
      typ: 'single_line_text_field',
      auswahl: ['Ohne', 'Diamant', 'Saphir', 'Rubin', 'Smaragd', 'Perle', 'Aquamarin', 'Amethyst', 'Topas', 'Turmalin', 'Tansanit', 'Opal', 'Granat', 'Zirkonia', 'Sonstiger'],
      werte: { none: 'Ohne', diamond: 'Diamant', diamonds: 'Diamant', brillant: 'Diamant', brillanten: 'Diamant', sapphire: 'Saphir', ruby: 'Rubin', emerald: 'Smaragd', pearl: 'Perle', aquamarine: 'Aquamarin', amethyst: 'Amethyst', topaz: 'Topas', tourmaline: 'Turmalin', tanzanite: 'Tansanit', opal: 'Opal', garnet: 'Granat', zirconia: 'Zirkonia', 'cubic zirconia': 'Zirkonia' },
      gruppe: 'Edelsteine',
      quellen: ['Edelstein', 'Stein', 'Hauptstein', 'Gemstone', 'Stone', 'Main stone', 'Besatz'],
      ki: 'Hauptstein; „Ohne“ bei Schmuck ohne Steine.',
    },
    {
      schluessel: 'schliff',
      name: 'Schliff',
      typ: 'single_line_text_field',
      auswahl: ['Brillant', 'Princess', 'Smaragd', 'Oval', 'Tropfen', 'Herz', 'Marquise', 'Kissen', 'Baguette', 'Cabochon'],
      werte: { 'round brilliant': 'Brillant', brilliant: 'Brillant', round: 'Brillant', princess: 'Princess', emerald: 'Smaragd', oval: 'Oval', pear: 'Tropfen', heart: 'Herz', marquise: 'Marquise', cushion: 'Kissen', baguette: 'Baguette', cabochon: 'Cabochon' },
      gruppe: 'Edelsteine',
      quellen: ['Schliff', 'Cut', 'Shape', 'Form'],
      ki: 'Schliffform des Hauptsteins.',
    },
    {
      schluessel: 'karat',
      name: 'Karat (Steine)',
      typ: 'number_decimal',
      einheit: 'ct',
      gruppe: 'Edelsteine',
      quellen: ['Karat', 'Carat', 'Gesamtkarat', 'Total carat weight', 'ct', 'Steingewicht', 'carat_weight'],
      ki: 'Gesamtgewicht der Steine in Karat (z. B. 0.5); nicht mit Goldkarat verwechseln.',
    },
    {
      schluessel: 'reinheit',
      name: 'Reinheit',
      typ: 'single_line_text_field',
      auswahl: ['IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'P1', 'P2', 'P3'],
      werte: { fl: 'IF', 'lupenrein': 'IF', i1: 'P1', i2: 'P2', i3: 'P3', pi1: 'P1', pi2: 'P2', pi3: 'P3' },
      gruppe: 'Edelsteine',
      quellen: ['Reinheit', 'Clarity', 'Diamond clarity'],
      ki: 'Reinheitsgrad des Diamanten nach GIA/HRD.',
    },
    {
      schluessel: 'farbe',
      name: 'Farbe',
      typ: 'single_line_text_field',
      gruppe: 'Edelsteine',
      quellen: ['Farbe', 'Steinfarbe', 'Color', 'Colour', 'Diamond color', 'Farbgrad'],
      ki: 'Farbgrad bei Diamanten (D bis M) bzw. Farbe des Steins.',
    },
    {
      schluessel: 'ringgroesse',
      name: 'Ringgröße',
      typ: 'number_integer',
      einheit: 'EU',
      gruppe: 'Maße',
      quellen: ['Ringgröße', 'Ringgroesse', 'Ring size', 'Größe', 'Size', 'ring_size'],
      ki: 'Ringgröße nach Umfang in mm (EU), z. B. 54.',
    },
    {
      schluessel: 'laenge',
      name: 'Länge',
      typ: 'number_decimal',
      einheit: 'cm',
      gruppe: 'Maße',
      quellen: ['Länge', 'Kettenlänge', 'Length', 'Chain length', 'Armbandlänge'],
      ki: 'Länge von Kette oder Armband in Zentimetern.',
    },

    ...gpsr(),
  ],
};

/* ---------------------------------------------------------------- Fashion */

const FASHION: BranchenVorlage = {
  branche: 'fashion',
  gruppen: ['Zustand', 'Produktdetails', 'Größe & Passform', 'Material & Pflege', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: true, ausrichtung: 'Mode, Schuhe und Accessoires', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    {
      ...zustand('Zustand', ['Neu mit Etikett', 'Neu ohne Etikett', 'Sehr gut', 'Gut', 'Getragen']),
      pflicht: false,
      werte: { new: 'Neu mit Etikett', neu: 'Neu mit Etikett', 'new with tags': 'Neu mit Etikett', nwt: 'Neu mit Etikett', 'new without tags': 'Neu ohne Etikett', 'very good': 'Sehr gut', good: 'Gut', used: 'Getragen', 'pre-owned': 'Getragen', gebraucht: 'Getragen' },
      ki: 'Zustand nur aus ausdrücklichen Angaben übernehmen; Neuware aus dem Sortiment eines Händlers ist „Neu mit Etikett“.',
    },
    {
      schluessel: 'modell',
      name: 'Modell',
      typ: 'single_line_text_field',
      gruppe: 'Produktdetails',
      quellen: ['Modell', 'Model', 'Modellname', 'Style', 'Artikelname', 'Style name'],
      ki: 'Modellname ohne Marke, Farbe und Größe.',
    },
    geschlecht('Produktdetails', ['Damen', 'Herren', 'Unisex', 'Mädchen', 'Jungen', 'Baby']),
    {
      schluessel: 'farbe',
      name: 'Farbe',
      typ: 'single_line_text_field',
      auswahl: FARBEN.filter((f) => !['Champagner', 'Perlmutt'].includes(f)),
      werte: FARB_WERTE,
      gruppe: 'Produktdetails',
      quellen: ['Farbe', 'Color', 'Colour', 'Hauptfarbe', 'Main color', 'color'],
      ki: 'Hauptfarbe des Artikels.',
    },
    {
      schluessel: 'muster',
      name: 'Muster',
      typ: 'single_line_text_field',
      auswahl: ['Uni', 'Gestreift', 'Kariert', 'Geblümt', 'Gepunktet', 'Animal-Print', 'Camouflage', 'Logo-Print', 'Gemustert'],
      werte: { solid: 'Uni', plain: 'Uni', einfarbig: 'Uni', striped: 'Gestreift', stripes: 'Gestreift', checked: 'Kariert', plaid: 'Kariert', floral: 'Geblümt', dotted: 'Gepunktet', 'polka dot': 'Gepunktet', leopard: 'Animal-Print', 'animal print': 'Animal-Print', camo: 'Camouflage', logo: 'Logo-Print', patterned: 'Gemustert' },
      gruppe: 'Produktdetails',
      quellen: ['Muster', 'Pattern', 'Print', 'Design'],
      ki: 'Muster des Stoffs.',
    },
    {
      schluessel: 'saison',
      name: 'Saison',
      typ: 'single_line_text_field',
      auswahl: ['Frühjahr/Sommer', 'Herbst/Winter', 'Ganzjährig'],
      werte: { ss: 'Frühjahr/Sommer', 'spring/summer': 'Frühjahr/Sommer', sommer: 'Frühjahr/Sommer', summer: 'Frühjahr/Sommer', fw: 'Herbst/Winter', aw: 'Herbst/Winter', 'fall/winter': 'Herbst/Winter', 'autumn/winter': 'Herbst/Winter', winter: 'Herbst/Winter', 'all season': 'Ganzjährig', ganzjaehrig: 'Ganzjährig' },
      gruppe: 'Produktdetails',
      quellen: ['Saison', 'Season', 'Kollektion Saison'],
      ki: 'Saison, für die der Artikel gedacht ist.',
    },
    {
      schluessel: 'groesse',
      name: 'Größe',
      typ: 'single_line_text_field',
      gruppe: 'Größe & Passform',
      quellen: ['Größe', 'Groesse', 'Size', 'Konfektionsgröße', 'size'],
      ki: 'Größe wie auf dem Etikett (z. B. „M“, „38“, „32/34“); bei mehreren Größen über die Varianten pflegen.',
    },
    {
      schluessel: 'groessensystem',
      name: 'Größensystem',
      typ: 'single_line_text_field',
      auswahl: ['EU', 'DE', 'IT', 'FR', 'UK', 'US', 'International (XS–XXL)', 'Jeans (W/L)'],
      werte: { international: 'International (XS–XXL)', int: 'International (XS–XXL)', jeans: 'Jeans (W/L)', 'w/l': 'Jeans (W/L)' },
      gruppe: 'Größe & Passform',
      quellen: ['Größensystem', 'Size system', 'Size standard', 'Größentabelle'],
      ki: 'Größensystem der Größenangabe.',
    },
    {
      schluessel: 'passform',
      name: 'Passform',
      typ: 'single_line_text_field',
      auswahl: ['Slim Fit', 'Regular Fit', 'Relaxed Fit', 'Oversize', 'Skinny', 'Straight', 'Wide Leg'],
      werte: { slim: 'Slim Fit', regular: 'Regular Fit', normal: 'Regular Fit', relaxed: 'Relaxed Fit', loose: 'Relaxed Fit', oversized: 'Oversize', skinny: 'Skinny', straight: 'Straight', wide: 'Wide Leg' },
      gruppe: 'Größe & Passform',
      quellen: ['Passform', 'Fit', 'Schnitt', 'Cut'],
      ki: 'Passform laut Hersteller.',
    },
    {
      schluessel: 'material',
      name: 'Material / Zusammensetzung',
      typ: 'single_line_text_field',
      gruppe: 'Material & Pflege',
      pflicht: true,
      quellen: ['Material', 'Zusammensetzung', 'Materialzusammensetzung', 'Composition', 'Fabric', 'Obermaterial', 'material'],
      ki: 'Faserzusammensetzung nach Textilkennzeichnungsverordnung mit Anteilen in absteigender Reihenfolge, z. B. „95 % Baumwolle, 5 % Elasthan“; nur aus Etikett oder Herstellerangaben.',
      beschreibung: 'Pflichtangabe für Textilien (Verordnung (EU) Nr. 1007/2011) – vor dem Kauf sichtbar.',
    },
    {
      schluessel: 'tierische_teile',
      name: 'Enthält nichttextile Teile tierischen Ursprungs',
      typ: 'boolean',
      gruppe: 'Material & Pflege',
      quellen: ['Tierische Bestandteile', 'Nichttextile Teile tierischen Ursprungs', 'Contains non-textile parts of animal origin', 'Echtleder', 'Echtfell'],
      ki: 'Ja bei Leder, Fell, Horn o. Ä. als Besatz (Pflichthinweis der Textilkennzeichnung).',
    },
    {
      schluessel: 'pflegehinweis',
      name: 'Pflegehinweis',
      typ: 'multi_line_text_field',
      gruppe: 'Material & Pflege',
      quellen: ['Pflegehinweis', 'Pflege', 'Pflegehinweise', 'Care instructions', 'Care', 'Waschhinweis'],
      ki: 'Pflege laut Etikett, z. B. „Maschinenwäsche 30 °C, nicht im Trockner trocknen“.',
    },

    herkunftsland('Material & Pflege'),
    ...gpsr(),
  ],
};

/* ---------------------------------------------------------------- weitere Bausteine */

/** Gefahrstoffe (CLP-Verordnung Art. 48: Gefahren auch im Fernabsatz vor dem Kauf angeben) */
const gefahrstoff = (gruppe: string): VorlageFeld[] => [
  {
    schluessel: 'gefahrenpiktogramme',
    name: 'Gefahrenpiktogramme (CLP)',
    typ: 'list.single_line_text_field',
    auswahl: ['GHS01 Explosiv', 'GHS02 Entzündbar', 'GHS03 Brandfördernd', 'GHS04 Gas unter Druck', 'GHS05 Ätzend', 'GHS06 Giftig', 'GHS07 Reizend', 'GHS08 Gesundheitsgefahr', 'GHS09 Umweltgefährlich', 'Keine'],
    werte: { ghs01: 'GHS01 Explosiv', ghs02: 'GHS02 Entzündbar', ghs03: 'GHS03 Brandfördernd', ghs04: 'GHS04 Gas unter Druck', ghs05: 'GHS05 Ätzend', ghs06: 'GHS06 Giftig', ghs07: 'GHS07 Reizend', ghs08: 'GHS08 Gesundheitsgefahr', ghs09: 'GHS09 Umweltgefährlich', none: 'Keine' },
    gruppe,
    quellen: ['Gefahrenpiktogramme', 'GHS', 'Piktogramme', 'Hazard pictograms', 'hazard_pictograms'],
    ki: 'Nur Piktogramme, die laut Etikett oder Sicherheitsdatenblatt aufgedruckt sind; nie raten.',
  },
  {
    schluessel: 'signalwort',
    name: 'Signalwort',
    typ: 'single_line_text_field',
    auswahl: ['Gefahr', 'Achtung'],
    werte: { danger: 'Gefahr', warning: 'Achtung' },
    gruppe,
    quellen: ['Signalwort', 'Signal word', 'signal_word'],
    ki: 'Signalwort laut Etikett: „Gefahr“ oder „Achtung“.',
  },
  {
    schluessel: 'gefahrenhinweise',
    name: 'Gefahren- und Sicherheitshinweise (H/P-Sätze)',
    typ: 'multi_line_text_field',
    gruppe,
    quellen: ['Gefahrenhinweise', 'H-Sätze', 'P-Sätze', 'Hazard statements', 'Precautionary statements'],
    ki: 'H- und P-Sätze wörtlich vom Etikett übernehmen.',
  },
];

const lebensmittelunternehmer = (gruppe: string): VorlageFeld => ({
  schluessel: 'lebensmittelunternehmer',
  name: 'Lebensmittelunternehmer (Name und Anschrift)',
  typ: 'multi_line_text_field',
  gruppe,
  pflicht: true,
  quellen: ['Lebensmittelunternehmer', 'Inverkehrbringer', 'Hersteller', 'Herstelleranschrift', 'Manufacturer', 'Food business operator'],
  ki: 'Name und Anschrift des Lebensmittelunternehmers laut Verpackung; nie erfinden.',
  beschreibung: 'Pflichtangabe nach LMIV Art. 9 – für Lebensmittel anstelle der GPSR-Angaben.',
});

const verkehrsbezeichnung = (gruppe: string, ki: string): VorlageFeld => ({
  schluessel: 'verkehrsbezeichnung',
  name: 'Bezeichnung des Lebensmittels',
  typ: 'single_line_text_field',
  gruppe,
  pflicht: true,
  quellen: ['Bezeichnung', 'Verkehrsbezeichnung', 'Rechtliche Bezeichnung', 'Legal name', 'Product denomination'],
  ki,
});

const lagerung = (gruppe: string): VorlageFeld => ({
  schluessel: 'lagerhinweis',
  name: 'Lagerung und Aufbewahrung',
  typ: 'multi_line_text_field',
  gruppe,
  quellen: ['Lagerung', 'Lagerhinweis', 'Aufbewahrung', 'Storage', 'Storage instructions'],
  ki: 'Lager- und Verwendungshinweise laut Verpackung, z. B. „Kühl und trocken lagern. Nach dem Öffnen innerhalb von 3 Tagen verbrauchen.“',
});

const ernaehrung = (gruppe: string): VorlageFeld => ({
  schluessel: 'ernaehrungsform',
  name: 'Ernährungsform',
  typ: 'list.single_line_text_field',
  auswahl: ['Vegan', 'Vegetarisch', 'Bio', 'Glutenfrei', 'Laktosefrei', 'Zuckerfrei', 'Halal', 'Koscher'],
  werte: { vegan: 'Vegan', vegetarian: 'Vegetarisch', organic: 'Bio', 'gluten free': 'Glutenfrei', 'gluten-free': 'Glutenfrei', 'lactose free': 'Laktosefrei', 'sugar free': 'Zuckerfrei' },
  gruppe,
  quellen: ['Ernährungsform', 'Eigenschaften', 'Diet', 'Dietary', 'Labels'],
  ki: 'Nur ausdrücklich angegebene oder zertifizierte Eigenschaften; „Bio“ nur mit Öko-Kontrollstelle.',
});

const geschmack = (gruppe: string): VorlageFeld => ({
  schluessel: 'geschmack',
  name: 'Geschmack / Sorte',
  typ: 'single_line_text_field',
  gruppe,
  quellen: ['Geschmack', 'Sorte', 'Geschmacksrichtung', 'Flavour', 'Flavor', 'Taste'],
  ki: 'Geschmacksrichtung bzw. Sorte, z. B. „Schokolade“.',
});

const groesse = (gruppe: string, o: { name?: string; ki?: string } = {}): VorlageFeld => ({
  schluessel: 'groesse',
  name: o.name ?? 'Größe',
  typ: 'single_line_text_field',
  gruppe,
  quellen: ['Größe', 'Groesse', 'Size', 'Konfektionsgröße', 'size'],
  ki: o.ki ?? 'Größe wie auf dem Etikett (z. B. „M“, „38“); mehrere Größen über die Varianten pflegen.',
});

/* ---------------------------------------------------------------- Schuhe */

const SCHUHMATERIAL = ['Leder', 'Beschichtetes Leder', 'Textil', 'Sonstiges Material'];
const SCHUHMATERIAL_WERTE: Record<string, string> = { leather: 'Leder', glattleder: 'Leder', wildleder: 'Leder', veloursleder: 'Leder', suede: 'Leder', 'coated leather': 'Beschichtetes Leder', textile: 'Textil', fabric: 'Textil', mesh: 'Textil', synthetic: 'Sonstiges Material', synthetik: 'Sonstiges Material', gummi: 'Sonstiges Material', rubber: 'Sonstiges Material', kunststoff: 'Sonstiges Material' };
const schuhteil = (schluessel: string, name: string, quellen: string[]): VorlageFeld => ({
  schluessel, name, typ: 'single_line_text_field', auswahl: SCHUHMATERIAL, werte: SCHUHMATERIAL_WERTE, gruppe: 'Material', pflicht: true, quellen,
  ki: 'Material nach Schuhkennzeichnung (Richtlinie 94/11/EG), das mindestens 80 % ausmacht; nur aus Etikett oder Herstellerangaben.',
});

const SCHUHE: BranchenVorlage = {
  branche: 'schuhe',
  gruppen: ['Zustand', 'Produktdetails', 'Größe & Passform', 'Material', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: true, ausrichtung: 'Schuhe für Damen, Herren und Kinder', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    zustandWare('Zustand'),
    modell('Produktdetails'),
    {
      schluessel: 'schuhart',
      name: 'Schuhart',
      typ: 'single_line_text_field',
      auswahl: ['Sneaker', 'Halbschuhe', 'Business-Schuhe', 'Stiefel', 'Stiefeletten', 'Pumps', 'Sandalen', 'Ballerinas', 'Slipper & Loafer', 'Hausschuhe', 'Laufschuhe', 'Sportschuhe', 'Wanderschuhe', 'Sicherheitsschuhe', 'Gummistiefel'],
      werte: { sneakers: 'Sneaker', boots: 'Stiefel', 'ankle boots': 'Stiefeletten', sandals: 'Sandalen', loafers: 'Slipper & Loafer', slippers: 'Hausschuhe', 'running shoes': 'Laufschuhe', 'hiking boots': 'Wanderschuhe' },
      gruppe: 'Produktdetails',
      quellen: ['Schuhart', 'Schuhtyp', 'Art', 'Shoe type', 'Type', 'Produktart'],
      ki: 'Art des Schuhs.',
    },
    geschlecht('Produktdetails', ['Damen', 'Herren', 'Unisex', 'Mädchen', 'Jungen', 'Baby']),
    farbe('Produktdetails'),
    groesse('Größe & Passform', { name: 'Schuhgröße', ki: 'Schuhgröße wie auf dem Etikett (z. B. „42“, „8,5“); mehrere Größen über die Varianten pflegen.' }),
    {
      schluessel: 'groessensystem',
      name: 'Größensystem',
      typ: 'single_line_text_field',
      auswahl: ['EU', 'UK', 'US', 'Mondopoint'],
      gruppe: 'Größe & Passform',
      quellen: ['Größensystem', 'Size system', 'Size standard'],
      ki: 'Größensystem der Größenangabe.',
    },
    {
      schluessel: 'weite',
      name: 'Weite',
      typ: 'single_line_text_field',
      auswahl: ['Schmal', 'Normal', 'Weit', 'Extra weit'],
      werte: { narrow: 'Schmal', regular: 'Normal', medium: 'Normal', wide: 'Weit', 'extra wide': 'Extra weit', f: 'Normal', g: 'Weit', h: 'Extra weit' },
      gruppe: 'Größe & Passform',
      quellen: ['Weite', 'Schuhweite', 'Width', 'Fit width'],
      ki: 'Weite laut Hersteller.',
    },
    { schluessel: 'absatzhoehe', name: 'Absatzhöhe', typ: 'number_decimal', einheit: 'cm', gruppe: 'Größe & Passform', quellen: ['Absatzhöhe', 'Absatz', 'Heel height'], ki: 'Absatzhöhe in Zentimetern.' },
    {
      schluessel: 'verschluss',
      name: 'Verschluss',
      typ: 'single_line_text_field',
      auswahl: ['Schnürung', 'Klettverschluss', 'Reißverschluss', 'Schnalle', 'Ohne (Slip-on)', 'Elastikeinsatz'],
      werte: { laces: 'Schnürung', 'lace-up': 'Schnürung', velcro: 'Klettverschluss', zip: 'Reißverschluss', zipper: 'Reißverschluss', buckle: 'Schnalle', 'slip-on': 'Ohne (Slip-on)' },
      gruppe: 'Größe & Passform',
      quellen: ['Verschluss', 'Closure', 'Fastening'],
      ki: 'Art des Verschlusses.',
    },
    schuhteil('obermaterial', 'Obermaterial', ['Obermaterial', 'Upper', 'Upper material', 'Schaft']),
    schuhteil('futter', 'Futter und Decksohle', ['Futter', 'Innenmaterial', 'Decksohle', 'Lining', 'Lining and sock', 'Innensohle']),
    schuhteil('laufsohle', 'Laufsohle', ['Laufsohle', 'Sohle', 'Outsole', 'Sole']),
    herkunftsland('Material'),
    ...gpsr({ pflicht: true }),
  ],
};

/* ---------------------------------------------------------------- Beauty & Kosmetik */

const inhaltsstoffe = (gruppe: string, o: { name: string; ki: string; pflicht: boolean }): VorlageFeld => ({
  schluessel: 'inhaltsstoffe',
  name: o.name,
  typ: 'multi_line_text_field',
  gruppe,
  pflicht: o.pflicht,
  quellen: ['Inhaltsstoffe', 'INCI', 'Ingredients', 'Zusammensetzung', 'ingredients', 'Bestandteile'],
  ki: o.ki,
});

const pao = (gruppe: string): VorlageFeld => ({
  schluessel: 'pao',
  name: 'Haltbarkeit nach dem Öffnen (PAO)',
  typ: 'single_line_text_field',
  auswahl: ['3 Monate', '6 Monate', '9 Monate', '12 Monate', '18 Monate', '24 Monate', '36 Monate', 'Mindesthaltbarkeit angegeben'],
  werte: { '3m': '3 Monate', '6m': '6 Monate', '9m': '9 Monate', '12m': '12 Monate', '18m': '18 Monate', '24m': '24 Monate', '36m': '36 Monate', '3 m': '3 Monate', '6 m': '6 Monate', '12 m': '12 Monate', '24 m': '24 Monate' },
  gruppe,
  quellen: ['PAO', 'Haltbarkeit nach dem Öffnen', 'Period after opening', 'Haltbarkeit'],
  ki: 'Symbol „offener Tiegel“ (z. B. 12M); bei Mindesthaltbarkeit unter 30 Monaten „Mindesthaltbarkeit angegeben“.',
});

const BEAUTY: BranchenVorlage = {
  branche: 'beauty',
  gruppen: ['Produktdetails', 'Inhaltsstoffe & Anwendung', 'Inhalt & Haltbarkeit', 'Gefahrstoffe', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: true, ausrichtung: 'Beauty, Kosmetik und Pflege', beschreibungMin: 0, markePflicht: true },
  buyBoxMerkmale: null,
  felder: [
    {
      schluessel: 'produktart',
      name: 'Produktart',
      typ: 'single_line_text_field',
      auswahl: ['Gesichtspflege', 'Körperpflege', 'Make-up', 'Haarpflege', 'Parfum', 'Nagelpflege', 'Sonnenschutz', 'Herrenpflege', 'Zubehör'],
      werte: { skincare: 'Gesichtspflege', 'body care': 'Körperpflege', makeup: 'Make-up', 'make up': 'Make-up', haircare: 'Haarpflege', fragrance: 'Parfum', perfume: 'Parfum', duft: 'Parfum', nails: 'Nagelpflege', suncare: 'Sonnenschutz', 'sun care': 'Sonnenschutz' },
      gruppe: 'Produktdetails',
      quellen: ['Produktart', 'Kategorie', 'Product type', 'Type'],
      ki: 'Art des Kosmetikprodukts.',
    },
    modell('Produktdetails', { ki: 'Produktlinie bzw. Name des Produkts ohne Marke und Inhalt.' }),
    geschlecht('Produktdetails'),
    {
      schluessel: 'hauttyp',
      name: 'Haut- bzw. Haartyp',
      typ: 'list.single_line_text_field',
      auswahl: ['Alle Typen', 'Normal', 'Trocken', 'Fettig', 'Mischhaut', 'Sensibel', 'Reif', 'Unreine Haut', 'Coloriertes Haar', 'Lockiges Haar'],
      werte: { 'all skin types': 'Alle Typen', normal: 'Normal', dry: 'Trocken', oily: 'Fettig', combination: 'Mischhaut', sensitive: 'Sensibel', mature: 'Reif' },
      gruppe: 'Produktdetails',
      quellen: ['Hauttyp', 'Haartyp', 'Skin type', 'Hair type'],
      ki: 'Hauttyp bzw. Haartyp laut Hersteller; mehrere Werte möglich.',
    },
    { schluessel: 'farbton', name: 'Farbton', typ: 'single_line_text_field', gruppe: 'Produktdetails', quellen: ['Farbton', 'Farbe', 'Shade', 'Colour', 'Color'], ki: 'Farbton laut Hersteller, z. B. „02 Light Beige“.' },
    { schluessel: 'duftrichtung', name: 'Duftrichtung', typ: 'single_line_text_field', gruppe: 'Produktdetails', quellen: ['Duftrichtung', 'Duft', 'Duftnote', 'Scent', 'Fragrance family'], ki: 'Duftrichtung bzw. Duftnoten, z. B. „Holzig, Zitrus“.' },
    { schluessel: 'lichtschutzfaktor', name: 'Lichtschutzfaktor', typ: 'number_integer', einheit: 'LSF', gruppe: 'Produktdetails', quellen: ['Lichtschutzfaktor', 'LSF', 'SPF', 'Sun protection factor'], ki: 'Lichtschutzfaktor als Zahl (SPF 30 = 30).' },
    inhaltsstoffe('Inhaltsstoffe & Anwendung', { name: 'Inhaltsstoffe (INCI)', pflicht: true, ki: 'INCI-Liste wörtlich von Verpackung oder Hersteller übernehmen, Reihenfolge beibehalten; nie ergänzen oder erfinden.' }),
    anwendung('Inhaltsstoffe & Anwendung'),
    {
      schluessel: 'eigenschaften',
      name: 'Eigenschaften',
      typ: 'list.single_line_text_field',
      auswahl: ['Vegan', 'Zertifizierte Naturkosmetik', 'Ohne Duftstoffe', 'Ohne Parabene', 'Ohne Silikone', 'Dermatologisch getestet', 'Nachfüllbar'],
      werte: { vegan: 'Vegan', 'natural cosmetics': 'Zertifizierte Naturkosmetik', naturkosmetik: 'Zertifizierte Naturkosmetik', 'fragrance free': 'Ohne Duftstoffe', parfümfrei: 'Ohne Duftstoffe', 'paraben free': 'Ohne Parabene', 'silicone free': 'Ohne Silikone', refillable: 'Nachfüllbar' },
      gruppe: 'Inhaltsstoffe & Anwendung',
      quellen: ['Eigenschaften', 'Besonderheiten', 'Features', 'Claims'],
      ki: 'Nur ausdrücklich angegebene Eigenschaften; keine Werbeaussagen ergänzen.',
    },
    ...grundpreis('Inhalt & Haltbarkeit'),
    pao('Inhalt & Haltbarkeit'),
    ...gefahrstoff('Gefahrstoffe'),
    ...gpsr({ pflicht: true }),
  ],
};

/* ---------------------------------------------------------------- Elektronik & Technik */

const ELEKTRONIK: BranchenVorlage = {
  branche: 'elektronik',
  gruppen: ['Zustand & Garantie', 'Produktdetails', 'Technische Daten', 'Energie & Umwelt', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: true, ausrichtung: 'Elektronik, Computer und Haushaltsgeräte', beschreibungMin: 0, markePflicht: true },
  buyBoxMerkmale: null,
  felder: [
    zustandWare('Zustand & Garantie', { pflicht: true, generalueberholt: true, defekt: true }),
    zustandBeschreibung('Zustand & Garantie'),
    {
      schluessel: 'garantie',
      name: 'Garantie',
      typ: 'number_integer',
      einheit: 'Monate',
      gruppe: 'Zustand & Garantie',
      quellen: ['Garantie', 'Herstellergarantie', 'Warranty', 'Garantie (Monate)', 'warranty_months'],
      ki: 'Garantie des Herstellers oder Händlers in Monaten; Jahre umrechnen (2 Jahre = 24). Nicht mit der gesetzlichen Gewährleistung verwechseln.',
    },
    modell('Produktdetails', { pflicht: true, ki: 'Modellbezeichnung ohne Marke, z. B. „Galaxy S24“ oder „WH-1000XM5“.' }),
    mpn('Produktdetails'),
    farbe('Produktdetails'),
    { schluessel: 'speicherkapazitaet', name: 'Speicherkapazität', typ: 'number_integer', einheit: 'GB', gruppe: 'Technische Daten', quellen: ['Speicherkapazität', 'Speicher', 'Storage', 'Capacity', 'Interner Speicher'], ki: 'Speicher in GB; TB umrechnen (1 TB = 1000 GB).' },
    { schluessel: 'arbeitsspeicher', name: 'Arbeitsspeicher', typ: 'number_integer', einheit: 'GB', gruppe: 'Technische Daten', quellen: ['Arbeitsspeicher', 'RAM', 'Memory'], ki: 'Arbeitsspeicher in GB.' },
    { schluessel: 'bildschirmdiagonale', name: 'Bildschirmdiagonale', typ: 'number_decimal', einheit: 'Zoll', gruppe: 'Technische Daten', quellen: ['Bildschirmdiagonale', 'Displaygröße', 'Screen size', 'Display size'], ki: 'Diagonale in Zoll, nur die Zahl (z. B. 6.1).' },
    { schluessel: 'betriebssystem', name: 'Betriebssystem', typ: 'single_line_text_field', gruppe: 'Technische Daten', quellen: ['Betriebssystem', 'OS', 'Operating system'], ki: 'Betriebssystem mit Version, falls angegeben.' },
    {
      schluessel: 'anschluesse',
      name: 'Anschlüsse & Funk',
      typ: 'list.single_line_text_field',
      auswahl: ['USB-C', 'USB-A', 'HDMI', 'DisplayPort', 'Thunderbolt', 'Lightning', 'Klinke 3,5 mm', 'LAN', 'WLAN', 'Bluetooth', 'NFC', '5G', 'LTE'],
      werte: { 'usb type-c': 'USB-C', 'usb c': 'USB-C', wifi: 'WLAN', 'wi-fi': 'WLAN', ethernet: 'LAN', 'headphone jack': 'Klinke 3,5 mm', aux: 'Klinke 3,5 mm' },
      gruppe: 'Technische Daten',
      quellen: ['Anschlüsse', 'Schnittstellen', 'Konnektivität', 'Connectivity', 'Ports', 'Interfaces'],
      ki: 'Vorhandene Anschlüsse und Funkstandards; mehrere Werte möglich.',
    },
    { schluessel: 'leistung', name: 'Leistung', typ: 'number_decimal', einheit: 'W', gruppe: 'Technische Daten', quellen: ['Leistung', 'Leistungsaufnahme', 'Power', 'Wattage', 'Watt'], ki: 'Nennleistung bzw. Leistungsaufnahme in Watt.' },
    { schluessel: 'lieferumfang', name: 'Lieferumfang', typ: 'multi_line_text_field', gruppe: 'Technische Daten', quellen: ['Lieferumfang', 'Im Lieferumfang', "What's in the box", 'Scope of delivery', 'Contents'], ki: 'Teile im Lieferumfang laut Hersteller, z. B. „Gerät, USB-C-Kabel, Kurzanleitung“.' },
    ...energie('Energie & Umwelt'),
    ...batterie('Energie & Umwelt'),
    ...gpsr({ pflicht: true }),
  ],
};

/* ---------------------------------------------------------------- Auto & Motorrad */

const AUTO: BranchenVorlage = {
  branche: 'auto',
  gruppen: ['Zustand', 'Teil & Hersteller', 'Fahrzeug', 'Zulassung', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: false, ausrichtung: 'Autoteile, Motorradteile und Zubehör', beschreibungMin: 0, markePflicht: true },
  buyBoxMerkmale: ['marke', 'modell', 'zustand'],
  felder: [
    zustandWare('Zustand', { pflicht: true, generalueberholt: true, defekt: true }),
    zustandBeschreibung('Zustand'),
    { ...mpn('Teil & Hersteller', true), name: 'Hersteller-Teilenummer', ki: 'Teilenummer des Teileherstellers exakt übernehmen (z. B. „0 986 494 524“); keine OE-Nummer, keine SKU.' },
    { schluessel: 'oe_nummer', name: 'OE- / Vergleichsnummern', typ: 'single_line_text_field', gruppe: 'Teil & Hersteller', quellen: ['OE-Nummer', 'OEM-Nummer', 'Vergleichsnummer', 'OE number', 'OEM number', 'Reference numbers'], ki: 'Originalteilenummern der Fahrzeughersteller, mehrere mit Komma.' },
    material('Teil & Hersteller'),
    {
      schluessel: 'fahrzeugart',
      name: 'Fahrzeugart',
      typ: 'list.single_line_text_field',
      auswahl: ['PKW', 'Motorrad', 'Roller', 'Transporter', 'Wohnmobil', 'LKW', 'Quad'],
      werte: { car: 'PKW', auto: 'PKW', motorcycle: 'Motorrad', scooter: 'Roller', van: 'Transporter', camper: 'Wohnmobil', truck: 'LKW', atv: 'Quad' },
      gruppe: 'Fahrzeug',
      quellen: ['Fahrzeugart', 'Fahrzeugtyp', 'Vehicle type'],
      ki: 'Für welche Fahrzeugarten das Teil gedacht ist.',
    },
    { schluessel: 'fahrzeugeignung', name: 'Passend für', typ: 'multi_line_text_field', gruppe: 'Fahrzeug', quellen: ['Passend für', 'Fahrzeugeignung', 'Fahrzeugzuordnung', 'Kompatibilität', 'Fits', 'Compatibility', 'Vehicle fitment'], ki: 'Fahrzeuge mit Hersteller, Modell, Motor und Baujahren, z. B. „VW Golf VII 1.4 TSI (2012–2020)“; nur aus den Daten.' },
    { schluessel: 'hsn_tsn', name: 'HSN/TSN (KBA-Nummern)', typ: 'single_line_text_field', gruppe: 'Fahrzeug', quellen: ['HSN/TSN', 'KBA-Nummer', 'KBA', 'HSN', 'TSN'], ki: 'Schlüsselnummern aus dem Fahrzeugschein, z. B. „0603/BMS“, mehrere mit Komma.' },
    {
      schluessel: 'einbauposition',
      name: 'Einbauposition',
      typ: 'list.single_line_text_field',
      auswahl: ['Vorne', 'Hinten', 'Links', 'Rechts', 'Oben', 'Unten', 'Innen', 'Außen'],
      werte: { front: 'Vorne', rear: 'Hinten', left: 'Links', right: 'Rechts', upper: 'Oben', lower: 'Unten', inner: 'Innen', outer: 'Außen' },
      gruppe: 'Fahrzeug',
      quellen: ['Einbauposition', 'Einbauseite', 'Position', 'Placement on vehicle'],
      ki: 'Einbauposition am Fahrzeug; mehrere Werte möglich.',
    },
    {
      schluessel: 'zulassung',
      name: 'Zulassung / Prüfzeichen',
      typ: 'list.single_line_text_field',
      auswahl: ['ABE', 'ABG', 'EG-Typgenehmigung', 'E-Prüfzeichen', 'Teilegutachten', 'Eintragung erforderlich', 'Nicht erforderlich'],
      werte: { 'e-mark': 'E-Prüfzeichen', 'e-prüfzeichen': 'E-Prüfzeichen', ece: 'E-Prüfzeichen', tüv: 'Teilegutachten', gutachten: 'Teilegutachten' },
      gruppe: 'Zulassung',
      quellen: ['Zulassung', 'ABE', 'Prüfzeichen', 'Approval', 'E-mark', 'Straßenzulassung'],
      ki: 'Art der Zulassung für den Straßenverkehr; nur aus ausdrücklichen Angaben.',
    },
    { schluessel: 'altteilpfand', name: 'Altteilpfand', typ: 'number_decimal', einheit: '€', gruppe: 'Zulassung', quellen: ['Altteilpfand', 'Pfand', 'Core charge', 'Deposit'], ki: 'Pfand für das Altteil in Euro, nur die Zahl.' },
    ...gpsr({ pflicht: true }),
  ],
};

/* ---------------------------------------------------------------- Möbel & Wohnen */

const MOEBEL: BranchenVorlage = {
  branche: 'moebel',
  gruppen: ['Produktdetails', 'Maße & Gewicht', 'Material & Farbe', 'Aufbau & Lieferung', 'Energie & Umwelt', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: false, ausrichtung: 'Möbel, Leuchten und Wohnaccessoires', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    zustandWare('Produktdetails'),
    {
      schluessel: 'moebelart',
      name: 'Produktart',
      typ: 'single_line_text_field',
      auswahl: ['Sofa', 'Sessel', 'Stuhl', 'Tisch', 'Bett', 'Matratze', 'Schrank', 'Kommode', 'Regal', 'Sideboard', 'Garderobe', 'Leuchte', 'Teppich', 'Gartenmöbel', 'Wohnaccessoire', 'Sonstiges'],
      werte: { couch: 'Sofa', armchair: 'Sessel', chair: 'Stuhl', table: 'Tisch', bed: 'Bett', mattress: 'Matratze', wardrobe: 'Schrank', dresser: 'Kommode', shelf: 'Regal', lamp: 'Leuchte', lampe: 'Leuchte', rug: 'Teppich', carpet: 'Teppich', deko: 'Wohnaccessoire' },
      gruppe: 'Produktdetails',
      quellen: ['Produktart', 'Möbelart', 'Product type', 'Type'],
      ki: 'Art des Möbels bzw. Wohnartikels.',
    },
    modell('Produktdetails'),
    {
      schluessel: 'stil',
      name: 'Stil',
      typ: 'single_line_text_field',
      auswahl: ['Modern', 'Skandinavisch', 'Industrial', 'Landhaus', 'Klassisch', 'Vintage', 'Mid-Century', 'Boho'],
      werte: { scandinavian: 'Skandinavisch', industrial: 'Industrial', country: 'Landhaus', classic: 'Klassisch', retro: 'Vintage' },
      gruppe: 'Produktdetails',
      quellen: ['Stil', 'Style', 'Design'],
      ki: 'Einrichtungsstil, nur wenn erkennbar angegeben.',
    },
    {
      schluessel: 'raum',
      name: 'Raum',
      typ: 'list.single_line_text_field',
      auswahl: ['Wohnzimmer', 'Schlafzimmer', 'Esszimmer', 'Küche', 'Bad', 'Arbeitszimmer', 'Kinderzimmer', 'Flur', 'Garten & Balkon'],
      werte: { 'living room': 'Wohnzimmer', bedroom: 'Schlafzimmer', 'dining room': 'Esszimmer', kitchen: 'Küche', bathroom: 'Bad', office: 'Arbeitszimmer', 'home office': 'Arbeitszimmer', 'kids room': 'Kinderzimmer', hallway: 'Flur', outdoor: 'Garten & Balkon' },
      gruppe: 'Produktdetails',
      quellen: ['Raum', 'Zimmer', 'Room'],
      ki: 'Räume, für die der Artikel gedacht ist; mehrere Werte möglich.',
    },
    ...masse('Maße & Gewicht'),
    { schluessel: 'sitzhoehe', name: 'Sitzhöhe', typ: 'number_decimal', einheit: 'cm', gruppe: 'Maße & Gewicht', quellen: ['Sitzhöhe', 'Seat height'], ki: 'Sitzhöhe in Zentimetern.' },
    { schluessel: 'max_belastung', name: 'Maximale Belastbarkeit', typ: 'number_decimal', einheit: 'kg', gruppe: 'Maße & Gewicht', quellen: ['Belastbarkeit', 'Max. Belastung', 'Tragkraft', 'Max load', 'Weight capacity'], ki: 'Maximale Belastung in Kilogramm.' },
    material('Material & Farbe'),
    farbe('Material & Farbe'),
    { schluessel: 'bezug', name: 'Bezug', typ: 'single_line_text_field', gruppe: 'Material & Farbe', quellen: ['Bezug', 'Bezugsmaterial', 'Stoff', 'Upholstery', 'Cover material'], ki: 'Bezugsmaterial bei Polstermöbeln mit Zusammensetzung, z. B. „100 % Polyester“.' },
    {
      schluessel: 'aufbau',
      name: 'Aufbau',
      typ: 'single_line_text_field',
      auswahl: ['Montiert', 'Teilmontiert', 'Selbstmontage', 'Aufbauservice möglich'],
      werte: { assembled: 'Montiert', 'partly assembled': 'Teilmontiert', 'self assembly': 'Selbstmontage', 'assembly required': 'Selbstmontage', zerlegt: 'Selbstmontage' },
      gruppe: 'Aufbau & Lieferung',
      quellen: ['Aufbau', 'Montage', 'Lieferzustand', 'Assembly'],
      ki: 'Lieferzustand bzw. Aufbau.',
    },
    { schluessel: 'pakete', name: 'Anzahl Pakete', typ: 'number_integer', gruppe: 'Aufbau & Lieferung', quellen: ['Pakete', 'Anzahl Pakete', 'Packstücke', 'Number of packages'], ki: 'Anzahl der Versandpakete.' },
    { schluessel: 'speditionsware', name: 'Speditionsware', typ: 'boolean', gruppe: 'Aufbau & Lieferung', quellen: ['Spedition', 'Speditionsware', 'Freight', 'Bulky item'], ki: 'Ja, wenn per Spedition statt Paket geliefert wird.' },
    ...energie('Energie & Umwelt'),
    ...gpsr({ pflicht: true }),
  ],
};

/* ---------------------------------------------------------------- Haus, Garten & Heimwerken */

const GARTEN: BranchenVorlage = {
  branche: 'garten',
  gruppen: ['Produktdetails', 'Technische Daten', 'Maße & Material', 'Inhalt & Grundpreis', 'Gefahrstoffe', 'Energie & Umwelt', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: true, ausrichtung: 'Haus, Garten und Heimwerken', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    zustandWare('Produktdetails'),
    modell('Produktdetails'),
    mpn('Produktdetails'),
    {
      schluessel: 'antrieb',
      name: 'Antrieb',
      typ: 'single_line_text_field',
      auswahl: ['Akku', 'Elektro (Netz)', 'Benzin', 'Druckluft', 'Solar', 'Manuell'],
      werte: { battery: 'Akku', cordless: 'Akku', akku: 'Akku', electric: 'Elektro (Netz)', corded: 'Elektro (Netz)', strom: 'Elektro (Netz)', petrol: 'Benzin', gas: 'Benzin', pneumatic: 'Druckluft', manual: 'Manuell', hand: 'Manuell' },
      gruppe: 'Technische Daten',
      quellen: ['Antrieb', 'Antriebsart', 'Energieversorgung', 'Power source'],
      ki: 'Antriebsart bei Geräten und Werkzeug.',
    },
    { schluessel: 'leistung', name: 'Leistung', typ: 'number_decimal', einheit: 'W', gruppe: 'Technische Daten', quellen: ['Leistung', 'Nennleistung', 'Power', 'Wattage', 'Watt'], ki: 'Nennleistung in Watt; kW umrechnen.' },
    { schluessel: 'spannung', name: 'Spannung', typ: 'number_decimal', einheit: 'V', gruppe: 'Technische Daten', quellen: ['Spannung', 'Akkuspannung', 'Voltage'], ki: 'Spannung in Volt (bei Akkugeräten die Akkuspannung).' },
    { schluessel: 'akku_system', name: 'Akku-System', typ: 'single_line_text_field', gruppe: 'Technische Daten', quellen: ['Akku-System', 'Akkuplattform', 'Battery platform', 'System'], ki: 'Akkuplattform des Herstellers, z. B. „18 V System“; ob Akku und Ladegerät enthalten sind, in den Lieferumfang.' },
    { schluessel: 'schallleistungspegel', name: 'Schallleistungspegel', typ: 'number_integer', einheit: 'dB(A)', gruppe: 'Technische Daten', quellen: ['Schallleistungspegel', 'Lautstärke', 'Sound power level', 'Noise level', 'LWA'], ki: 'Garantierter Schallleistungspegel in dB(A).' },
    { schluessel: 'lieferumfang', name: 'Lieferumfang', typ: 'multi_line_text_field', gruppe: 'Technische Daten', quellen: ['Lieferumfang', 'Im Lieferumfang', 'Scope of delivery', 'Contents'], ki: 'Teile im Lieferumfang laut Hersteller, z. B. „ohne Akku und Ladegerät“.' },
    ...masse('Maße & Material'),
    material('Maße & Material'),
    farbe('Maße & Material'),
    {
      schluessel: 'einsatzbereich',
      name: 'Einsatzbereich',
      typ: 'list.single_line_text_field',
      auswahl: ['Innen', 'Außen', 'Garten', 'Werkstatt', 'Bad', 'Küche'],
      werte: { indoor: 'Innen', outdoor: 'Außen', garden: 'Garten', workshop: 'Werkstatt', bathroom: 'Bad', kitchen: 'Küche' },
      gruppe: 'Maße & Material',
      quellen: ['Einsatzbereich', 'Anwendungsbereich', 'Use', 'Application'],
      ki: 'Wo der Artikel eingesetzt wird; mehrere Werte möglich.',
    },
    ...grundpreis('Inhalt & Grundpreis', false),
    ...gefahrstoff('Gefahrstoffe'),
    ...energie('Energie & Umwelt'),
    ...batterie('Energie & Umwelt'),
    ...gpsr({ pflicht: true }),
  ],
};

/* ---------------------------------------------------------------- Sport & Fitness */

const SPORT: BranchenVorlage = {
  branche: 'sport',
  gruppen: ['Zustand', 'Produktdetails', 'Größe & Passform', 'Material & Technik', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: true, ausrichtung: 'Sport- und Fitnessartikel', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    zustandWare('Zustand'),
    modell('Produktdetails'),
    {
      schluessel: 'sportart',
      name: 'Sportart',
      typ: 'list.single_line_text_field',
      auswahl: ['Laufen', 'Fitness & Training', 'Fußball', 'Tennis', 'Golf', 'Schwimmen', 'Wassersport', 'Wintersport', 'Kampfsport', 'Yoga', 'Basketball', 'Teamsport', 'Klettern', 'Reiten', 'Sonstige'],
      werte: { running: 'Laufen', fitness: 'Fitness & Training', training: 'Fitness & Training', football: 'Fußball', soccer: 'Fußball', swimming: 'Schwimmen', 'water sports': 'Wassersport', ski: 'Wintersport', skiing: 'Wintersport', 'martial arts': 'Kampfsport', climbing: 'Klettern', riding: 'Reiten' },
      gruppe: 'Produktdetails',
      quellen: ['Sportart', 'Sport', 'Activity', 'Aktivität'],
      ki: 'Sportarten, für die der Artikel gedacht ist; mehrere Werte möglich.',
    },
    geschlecht('Produktdetails', ['Damen', 'Herren', 'Unisex', 'Mädchen', 'Jungen']),
    farbe('Produktdetails'),
    {
      schluessel: 'niveau',
      name: 'Niveau',
      typ: 'single_line_text_field',
      auswahl: ['Einsteiger', 'Fortgeschrittene', 'Profi'],
      werte: { beginner: 'Einsteiger', intermediate: 'Fortgeschrittene', advanced: 'Fortgeschrittene', pro: 'Profi', professional: 'Profi' },
      gruppe: 'Produktdetails',
      quellen: ['Niveau', 'Level', 'Leistungsstufe'],
      ki: 'Zielgruppe laut Hersteller.',
    },
    groesse('Größe & Passform'),
    material('Material & Technik', { name: 'Material / Zusammensetzung', ki: 'Material; bei Textilien die Faserzusammensetzung mit Anteilen, z. B. „88 % Polyester, 12 % Elasthan“.' }),
    { schluessel: 'max_belastung', name: 'Maximale Belastbarkeit', typ: 'number_decimal', einheit: 'kg', gruppe: 'Material & Technik', quellen: ['Max. Belastung', 'Maximales Benutzergewicht', 'Belastbarkeit', 'Max user weight', 'Max load'], ki: 'Maximales Benutzergewicht in Kilogramm (Fitnessgeräte).' },
    { schluessel: 'produktgewicht', name: 'Gewicht', typ: 'number_decimal', einheit: 'kg', gruppe: 'Material & Technik', quellen: ['Gewicht', 'Produktgewicht', 'Weight', 'Item weight'], ki: 'Gewicht des Produkts in Kilogramm.' },
    {
      schluessel: 'einsatzbereich',
      name: 'Einsatzbereich',
      typ: 'list.single_line_text_field',
      auswahl: ['Indoor', 'Outdoor', 'Halle', 'Rasen', 'Kunstrasen', 'Straße', 'Gelände'],
      werte: { indoor: 'Indoor', outdoor: 'Outdoor', 'firm ground': 'Rasen', fg: 'Rasen', ag: 'Kunstrasen', road: 'Straße', trail: 'Gelände' },
      gruppe: 'Material & Technik',
      quellen: ['Einsatzbereich', 'Untergrund', 'Surface', 'Terrain'],
      ki: 'Einsatzbereich bzw. Untergrund; mehrere Werte möglich.',
    },
    ...gpsr({ pflicht: true }),
  ],
};

/* ---------------------------------------------------------------- Outdoor & Camping */

const OUTDOOR: BranchenVorlage = {
  branche: 'outdoor',
  gruppen: ['Zustand', 'Produktdetails', 'Technische Daten', 'Material', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: true, ausrichtung: 'Outdoor-, Camping- und Trekkingausrüstung', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    zustandWare('Zustand'),
    modell('Produktdetails'),
    {
      schluessel: 'outdoorart',
      name: 'Produktart',
      typ: 'single_line_text_field',
      auswahl: ['Zelt', 'Schlafsack', 'Isomatte', 'Rucksack', 'Jacke', 'Hose', 'Schuhe', 'Kocher & Geschirr', 'Lampe', 'Campingmöbel', 'Navigation', 'Wasserfilter', 'Zubehör'],
      werte: { tent: 'Zelt', 'sleeping bag': 'Schlafsack', 'sleeping pad': 'Isomatte', backpack: 'Rucksack', jacket: 'Jacke', pants: 'Hose', trousers: 'Hose', shoes: 'Schuhe', stove: 'Kocher & Geschirr', lamp: 'Lampe', headlamp: 'Lampe', stirnlampe: 'Lampe', 'camping furniture': 'Campingmöbel' },
      gruppe: 'Produktdetails',
      quellen: ['Produktart', 'Kategorie', 'Product type', 'Type'],
      ki: 'Art der Ausrüstung.',
    },
    geschlecht('Produktdetails'),
    farbe('Produktdetails'),
    groesse('Produktdetails'),
    { schluessel: 'personen', name: 'Personen', typ: 'number_integer', gruppe: 'Technische Daten', quellen: ['Personen', 'Personenzahl', 'Schlafplätze', 'Persons', 'Capacity'], ki: 'Für wie viele Personen (Zelt, Kocher).' },
    { schluessel: 'wassersaeule', name: 'Wassersäule', typ: 'number_integer', einheit: 'mm', gruppe: 'Technische Daten', quellen: ['Wassersäule', 'Wasserdichtigkeit', 'Water column', 'Hydrostatic head', 'Waterproof rating'], ki: 'Wassersäule in Millimetern.' },
    { schluessel: 'komforttemperatur', name: 'Komforttemperatur', typ: 'number_decimal', einheit: '°C', gruppe: 'Technische Daten', quellen: ['Komforttemperatur', 'Komfort', 'Comfort temperature', 'T-Comfort'], ki: 'Komforttemperatur nach EN ISO 23537 in °C (Schlafsack).' },
    { schluessel: 'limittemperatur', name: 'Limittemperatur', typ: 'number_decimal', einheit: '°C', gruppe: 'Technische Daten', quellen: ['Limittemperatur', 'Limit', 'Limit temperature', 'T-Limit'], ki: 'Limittemperatur nach EN ISO 23537 in °C.' },
    { schluessel: 'volumen', name: 'Volumen', typ: 'number_decimal', einheit: 'l', gruppe: 'Technische Daten', quellen: ['Volumen', 'Fassungsvermögen', 'Volume', 'Capacity (l)'], ki: 'Volumen in Litern (Rucksack, Tasche).' },
    { schluessel: 'produktgewicht', name: 'Gewicht', typ: 'number_decimal', einheit: 'kg', gruppe: 'Technische Daten', quellen: ['Gewicht', 'Mindestgewicht', 'Weight', 'Item weight', 'Trail weight'], ki: 'Gewicht in Kilogramm; Gramm umrechnen.' },
    { schluessel: 'packmass', name: 'Packmaß', typ: 'single_line_text_field', gruppe: 'Technische Daten', quellen: ['Packmaß', 'Packmass', 'Packed size', 'Pack size'], ki: 'Packmaß mit Einheit, z. B. „45 × 18 cm“.' },
    material('Material', { name: 'Material / Zusammensetzung', ki: 'Material; bei Textilien die Faserzusammensetzung mit Anteilen, Füllung bei Schlafsäcken (z. B. „90 % Daune, 10 % Feder“).' }),
    ...gpsr({ pflicht: true }),
  ],
};

/* ---------------------------------------------------------------- Fahrrad & E-Bike */

const FAHRRAD: BranchenVorlage = {
  branche: 'fahrrad',
  gruppen: ['Zustand', 'Produktdetails', 'Rahmen & Größe', 'Ausstattung', 'Motor & Akku', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: false, ausrichtung: 'Fahrräder, E-Bikes und Fahrradzubehör', beschreibungMin: 0, markePflicht: true },
  buyBoxMerkmale: ['marke', 'modell', 'groesse', 'zustand', 'jahr'],
  felder: [
    zustandWare('Zustand', { pflicht: true }),
    zustandBeschreibung('Zustand'),
    { schluessel: 'laufleistung', name: 'Laufleistung', typ: 'number_integer', einheit: 'km', gruppe: 'Zustand', quellen: ['Laufleistung', 'Kilometerstand', 'Gefahrene Kilometer', 'Mileage', 'Odometer'], ki: 'Gefahrene Kilometer bei Gebrauchträdern.' },
    modell('Produktdetails', { pflicht: true, ki: 'Modellname ohne Marke und Modelljahr, z. B. „Stereo Hybrid 140 HPC“.' }),
    { schluessel: 'modelljahr', name: 'Modelljahr', typ: 'jahr', gruppe: 'Produktdetails', quellen: ['Modelljahr', 'Jahr', 'Baujahr', 'Model year', 'Year'], ki: 'Modelljahr des Herstellers, vierstellig.' },
    {
      schluessel: 'fahrradtyp',
      name: 'Fahrradtyp',
      typ: 'single_line_text_field',
      auswahl: ['Citybike', 'Trekkingrad', 'Mountainbike', 'Rennrad', 'Gravelbike', 'Crossrad', 'Lastenrad', 'Faltrad', 'Kinderrad', 'Jugendrad', 'BMX', 'Zubehör'],
      werte: { 'city bike': 'Citybike', trekking: 'Trekkingrad', mtb: 'Mountainbike', 'mountain bike': 'Mountainbike', 'road bike': 'Rennrad', gravel: 'Gravelbike', cross: 'Crossrad', 'cargo bike': 'Lastenrad', cargo: 'Lastenrad', 'folding bike': 'Faltrad', 'kids bike': 'Kinderrad' },
      gruppe: 'Produktdetails',
      quellen: ['Fahrradtyp', 'Fahrradart', 'Kategorie', 'Bike type', 'Type'],
      ki: 'Art des Fahrrads; „Zubehör“ für Teile und Zubehör.',
    },
    { schluessel: 'elektro', name: 'E-Bike (Pedelec)', typ: 'boolean', gruppe: 'Produktdetails', quellen: ['E-Bike', 'Pedelec', 'Elektro', 'Electric', 'E-Bike (Pedelec)'], ki: 'Ja bei Pedelecs bzw. E-Bikes mit Motor.' },
    farbe('Produktdetails'),
    { schluessel: 'rahmengroesse', name: 'Rahmengröße', typ: 'single_line_text_field', gruppe: 'Rahmen & Größe', quellen: ['Rahmengröße', 'Rahmenhöhe', 'Größe', 'Frame size', 'Size'], ki: 'Rahmengröße wie vom Hersteller angegeben, z. B. „M“ oder „52 cm“.' },
    {
      schluessel: 'laufradgroesse',
      name: 'Laufradgröße',
      typ: 'single_line_text_field',
      auswahl: ['12"', '14"', '16"', '18"', '20"', '24"', '26"', '27,5"', '28"', '29"'],
      werte: { '12': '12"', '14': '14"', '16': '16"', '18': '18"', '20': '20"', '24': '24"', '26': '26"', '27.5': '27,5"', '27,5': '27,5"', '650b': '27,5"', '28': '28"', '700c': '28"', '29': '29"' },
      gruppe: 'Rahmen & Größe',
      quellen: ['Laufradgröße', 'Reifengröße', 'Wheel size', 'Laufrad'],
      ki: 'Laufradgröße in Zoll.',
    },
    {
      schluessel: 'rahmenmaterial',
      name: 'Rahmenmaterial',
      typ: 'single_line_text_field',
      auswahl: ['Aluminium', 'Carbon', 'Stahl', 'Titan'],
      werte: { aluminum: 'Aluminium', alu: 'Aluminium', aluminium: 'Aluminium', carbon: 'Carbon', steel: 'Stahl', chromoly: 'Stahl', titanium: 'Titan' },
      gruppe: 'Rahmen & Größe',
      quellen: ['Rahmenmaterial', 'Rahmen', 'Frame material', 'Frame'],
      ki: 'Material des Rahmens.',
    },
    {
      schluessel: 'rahmenform',
      name: 'Rahmenform',
      typ: 'single_line_text_field',
      auswahl: ['Diamant', 'Trapez', 'Tiefeinsteiger', 'Wave'],
      werte: { diamond: 'Diamant', herren: 'Diamant', trapeze: 'Trapez', 'step-through': 'Tiefeinsteiger', 'low step': 'Tiefeinsteiger' },
      gruppe: 'Rahmen & Größe',
      quellen: ['Rahmenform', 'Rahmentyp', 'Frame type'],
      ki: 'Rahmenform.',
    },
    { schluessel: 'schaltung', name: 'Schaltung', typ: 'single_line_text_field', gruppe: 'Ausstattung', quellen: ['Schaltung', 'Schaltwerk', 'Gruppe', 'Drivetrain', 'Gears', 'Derailleur'], ki: 'Schaltung mit Gangzahl, z. B. „Shimano Deore, 12-fach“.' },
    {
      schluessel: 'bremsen',
      name: 'Bremsen',
      typ: 'single_line_text_field',
      auswahl: ['Scheibenbremse hydraulisch', 'Scheibenbremse mechanisch', 'Felgenbremse', 'Rücktrittbremse', 'Trommelbremse'],
      werte: { 'hydraulic disc': 'Scheibenbremse hydraulisch', 'mechanical disc': 'Scheibenbremse mechanisch', 'rim brake': 'Felgenbremse', 'v-brake': 'Felgenbremse', 'coaster brake': 'Rücktrittbremse', rücktritt: 'Rücktrittbremse' },
      gruppe: 'Ausstattung',
      quellen: ['Bremsen', 'Bremse', 'Brakes', 'Brake type'],
      ki: 'Bremsentyp.',
    },
    {
      schluessel: 'federung',
      name: 'Federung',
      typ: 'single_line_text_field',
      auswahl: ['Ungefedert', 'Federgabel (Hardtail)', 'Vollgefedert (Fully)'],
      werte: { rigid: 'Ungefedert', starr: 'Ungefedert', hardtail: 'Federgabel (Hardtail)', 'full suspension': 'Vollgefedert (Fully)', fully: 'Vollgefedert (Fully)' },
      gruppe: 'Ausstattung',
      quellen: ['Federung', 'Suspension'],
      ki: 'Art der Federung.',
    },
    { schluessel: 'stvzo', name: 'StVZO-Ausstattung', typ: 'boolean', gruppe: 'Ausstattung', quellen: ['StVZO', 'StVZO-konform', 'Straßenausstattung'], ki: 'Ja, wenn Licht, Reflektoren und Klingel nach StVZO verbaut sind.' },
    { schluessel: 'produktgewicht', name: 'Gewicht', typ: 'number_decimal', einheit: 'kg', gruppe: 'Ausstattung', quellen: ['Gewicht', 'Fahrradgewicht', 'Weight'], ki: 'Gewicht des Fahrrads in Kilogramm.' },
    { schluessel: 'zulaessiges_gesamtgewicht', name: 'Zulässiges Gesamtgewicht', typ: 'number_decimal', einheit: 'kg', gruppe: 'Ausstattung', quellen: ['Zulässiges Gesamtgewicht', 'Max. Systemgewicht', 'Systemgewicht', 'Max total weight'], ki: 'Zulässiges Gesamtgewicht (Rad, Fahrer, Gepäck) in Kilogramm.' },
    { schluessel: 'motor', name: 'Motor', typ: 'single_line_text_field', gruppe: 'Motor & Akku', quellen: ['Motor', 'Antrieb', 'Motor model', 'Drive unit'], ki: 'Hersteller und Modell des Motors, z. B. „Bosch Performance Line CX“.' },
    {
      schluessel: 'motorposition',
      name: 'Motorposition',
      typ: 'single_line_text_field',
      auswahl: ['Mittelmotor', 'Hinterradnabe', 'Vorderradnabe'],
      werte: { 'mid drive': 'Mittelmotor', mid: 'Mittelmotor', 'rear hub': 'Hinterradnabe', heckmotor: 'Hinterradnabe', 'front hub': 'Vorderradnabe', frontmotor: 'Vorderradnabe' },
      gruppe: 'Motor & Akku',
      quellen: ['Motorposition', 'Motor position'],
      ki: 'Position des Motors.',
    },
    { schluessel: 'drehmoment', name: 'Drehmoment', typ: 'number_integer', einheit: 'Nm', gruppe: 'Motor & Akku', quellen: ['Drehmoment', 'Torque'], ki: 'Maximales Drehmoment in Nm.' },
    { schluessel: 'akku_energie', name: 'Akkukapazität', typ: 'number_integer', einheit: 'Wh', gruppe: 'Motor & Akku', quellen: ['Akkukapazität', 'Akku', 'Battery', 'Battery capacity', 'Wh'], ki: 'Energiegehalt des Akkus in Wattstunden.' },
    ...gpsr({ pflicht: true }),
  ],
};

/* ---------------------------------------------------------------- Spielwaren & Baby */

const SPIELWAREN: BranchenVorlage = {
  branche: 'spielwaren',
  gruppen: ['Zustand', 'Produktdetails', 'Alter & Sicherheit', 'Material & Maße', 'Batterien', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: true, ausrichtung: 'Spielwaren und Babyartikel', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    zustandWare('Zustand'),
    {
      schluessel: 'spielzeugart',
      name: 'Produktart',
      typ: 'single_line_text_field',
      auswahl: ['Bausteine', 'Puppen & Zubehör', 'Plüsch', 'Fahrzeuge', 'Gesellschaftsspiele', 'Puzzle', 'Lernspielzeug', 'Kreativ & Basteln', 'Outdoor-Spielzeug', 'Babyspielzeug', 'Elektronisches Spielzeug', 'Kinderwagen', 'Babyausstattung', 'Sonstiges'],
      werte: { 'building blocks': 'Bausteine', lego: 'Bausteine', dolls: 'Puppen & Zubehör', plush: 'Plüsch', 'stuffed animal': 'Plüsch', vehicles: 'Fahrzeuge', 'board games': 'Gesellschaftsspiele', brettspiel: 'Gesellschaftsspiele', puzzles: 'Puzzle', educational: 'Lernspielzeug', crafts: 'Kreativ & Basteln', stroller: 'Kinderwagen' },
      gruppe: 'Produktdetails',
      quellen: ['Produktart', 'Spielzeugart', 'Kategorie', 'Product type', 'Toy type'],
      ki: 'Art des Spielzeugs bzw. Babyartikels.',
    },
    { ...mpn('Produktdetails'), name: 'Artikel- bzw. Set-Nummer', ki: 'Artikel- oder Set-Nummer des Herstellers exakt übernehmen (z. B. „75313“); keine SKU, keine EAN.' },
    { schluessel: 'spieleranzahl', name: 'Spieleranzahl', typ: 'single_line_text_field', gruppe: 'Produktdetails', quellen: ['Spieleranzahl', 'Spieler', 'Players', 'Number of players'], ki: 'Spielerzahl, z. B. „2–4“.' },
    { schluessel: 'spieldauer', name: 'Spieldauer', typ: 'number_integer', einheit: 'min', gruppe: 'Produktdetails', quellen: ['Spieldauer', 'Playing time', 'Dauer'], ki: 'Durchschnittliche Spieldauer in Minuten.' },
    { schluessel: 'teile', name: 'Anzahl Teile', typ: 'number_integer', gruppe: 'Produktdetails', quellen: ['Teile', 'Anzahl Teile', 'Teileanzahl', 'Pieces', 'Piece count'], ki: 'Anzahl der Teile (Bausatz, Puzzle).' },
    altersempfehlung('Alter & Sicherheit', true),
    {
      schluessel: 'warnhinweis_alter',
      name: 'Warnhinweis Altersgruppe',
      typ: 'single_line_text_field',
      auswahl: ['Nicht für Kinder unter 36 Monaten geeignet', 'Nur unter unmittelbarer Aufsicht von Erwachsenen benutzen', 'Kein Warnhinweis erforderlich'],
      werte: { 'not suitable for children under 36 months': 'Nicht für Kinder unter 36 Monaten geeignet', '0-3': 'Nicht für Kinder unter 36 Monaten geeignet', 'adult supervision required': 'Nur unter unmittelbarer Aufsicht von Erwachsenen benutzen' },
      gruppe: 'Alter & Sicherheit',
      pflicht: true,
      quellen: ['Warnhinweis', 'Altersbeschränkung', 'Warning', 'Choking hazard', 'Erstickungsgefahr'],
      ki: 'Warnhinweis nach Spielzeugrichtlinie 2009/48/EG wie auf der Verpackung; die Begründung (z. B. „Erstickungsgefahr durch verschluckbare Kleinteile“) gehört in die Sicherheitshinweise.',
      beschreibung: 'Warnhinweise müssen vor dem Kauf sichtbar sein (Spielzeugrichtlinie Art. 11).',
    },
    { schluessel: 'ce_kennzeichnung', name: 'CE-Kennzeichnung', typ: 'boolean', gruppe: 'Alter & Sicherheit', quellen: ['CE', 'CE-Kennzeichnung', 'CE marking', 'CE-Zeichen'], ki: 'Ja, wenn das Produkt die CE-Kennzeichnung trägt.' },
    material('Material & Maße'),
    farbe('Material & Maße'),
    ...masse('Material & Maße'),
    ...batterie('Batterien'),
    ...gpsr({ pflicht: true }).map((f) => (f.schluessel === 'sicherheitshinweise' ? { ...f, pflicht: true } : f)),
  ],
};

/* ---------------------------------------------------------------- Tierbedarf */

const TIERBEDARF: BranchenVorlage = {
  branche: 'tierbedarf',
  gruppen: ['Produktdetails', 'Zusammensetzung', 'Fütterung', 'Inhalt & Grundpreis', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: true, ausrichtung: 'Tierbedarf: Futter und Zubehör', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    {
      schluessel: 'tierart',
      name: 'Tierart',
      typ: 'list.single_line_text_field',
      auswahl: ['Hund', 'Katze', 'Kleintier', 'Vogel', 'Fisch', 'Reptil', 'Pferd', 'Nutztier'],
      werte: { dog: 'Hund', dogs: 'Hund', cat: 'Katze', cats: 'Katze', 'small animal': 'Kleintier', nager: 'Kleintier', bird: 'Vogel', fish: 'Fisch', aquarium: 'Fisch', reptile: 'Reptil', horse: 'Pferd' },
      gruppe: 'Produktdetails',
      pflicht: true,
      quellen: ['Tierart', 'Tier', 'Für', 'Animal', 'Pet type', 'Species'],
      ki: 'Für welche Tierarten der Artikel gedacht ist; mehrere Werte möglich.',
    },
    {
      schluessel: 'produktart',
      name: 'Produktart',
      typ: 'single_line_text_field',
      auswahl: ['Alleinfuttermittel', 'Ergänzungsfuttermittel', 'Snack', 'Zubehör', 'Pflege & Hygiene', 'Spielzeug', 'Sonstiges'],
      werte: { 'complete feed': 'Alleinfuttermittel', alleinfutter: 'Alleinfuttermittel', 'complementary feed': 'Ergänzungsfuttermittel', ergänzungsfutter: 'Ergänzungsfuttermittel', treats: 'Snack', leckerli: 'Snack', accessories: 'Zubehör', toy: 'Spielzeug' },
      gruppe: 'Produktdetails',
      quellen: ['Produktart', 'Futtermittelart', 'Art', 'Product type'],
      ki: 'Bei Futter die Futtermittelart laut Etikett (Allein- oder Ergänzungsfuttermittel).',
    },
    {
      schluessel: 'futterform',
      name: 'Futterform',
      typ: 'single_line_text_field',
      auswahl: ['Trockenfutter', 'Nassfutter', 'Halbfeuchtfutter', 'Rohfutter (BARF)', 'Snack'],
      werte: { dry: 'Trockenfutter', 'dry food': 'Trockenfutter', wet: 'Nassfutter', 'wet food': 'Nassfutter', 'semi-moist': 'Halbfeuchtfutter', raw: 'Rohfutter (BARF)', barf: 'Rohfutter (BARF)' },
      gruppe: 'Produktdetails',
      quellen: ['Futterform', 'Futterart', 'Food type', 'Form'],
      ki: 'Form des Futters.',
    },
    {
      schluessel: 'lebensphase',
      name: 'Lebensphase',
      typ: 'list.single_line_text_field',
      auswahl: ['Welpe / Kitten', 'Junior', 'Adult', 'Senior', 'Alle Lebensphasen'],
      werte: { puppy: 'Welpe / Kitten', kitten: 'Welpe / Kitten', welpe: 'Welpe / Kitten', junior: 'Junior', adult: 'Adult', senior: 'Senior', 'all life stages': 'Alle Lebensphasen' },
      gruppe: 'Produktdetails',
      quellen: ['Lebensphase', 'Alter', 'Life stage', 'Age'],
      ki: 'Lebensphase laut Hersteller.',
    },
    geschmack('Produktdetails'),
    {
      schluessel: 'besonderheiten',
      name: 'Besonderheiten',
      typ: 'list.single_line_text_field',
      auswahl: ['Getreidefrei', 'Hypoallergen', 'Sensitiv', 'Monoprotein', 'Light', 'Bio', 'Vegetarisch'],
      werte: { 'grain free': 'Getreidefrei', 'grain-free': 'Getreidefrei', hypoallergenic: 'Hypoallergen', sensitive: 'Sensitiv', 'single protein': 'Monoprotein', organic: 'Bio', vegetarian: 'Vegetarisch' },
      gruppe: 'Produktdetails',
      quellen: ['Besonderheiten', 'Eigenschaften', 'Features', 'Diet'],
      ki: 'Nur ausdrücklich angegebene Eigenschaften.',
    },
    material('Produktdetails', { ki: 'Material bei Zubehör, z. B. „Nylon“ oder „Edelstahl“.' }),
    { schluessel: 'zusammensetzung', name: 'Zusammensetzung', typ: 'multi_line_text_field', gruppe: 'Zusammensetzung', quellen: ['Zusammensetzung', 'Zutaten', 'Composition', 'Ingredients'], ki: 'Zusammensetzung des Futters wörtlich vom Etikett (Futtermittelverordnung (EG) 767/2009); nie ergänzen.' },
    { schluessel: 'analytische_bestandteile', name: 'Analytische Bestandteile', typ: 'multi_line_text_field', gruppe: 'Zusammensetzung', quellen: ['Analytische Bestandteile', 'Inhaltsstoffe', 'Analytical constituents', 'Guaranteed analysis'], ki: 'Rohprotein, Rohfett, Rohfaser, Rohasche, Feuchte in % wie auf dem Etikett.' },
    { schluessel: 'zusatzstoffe', name: 'Zusatzstoffe', typ: 'multi_line_text_field', gruppe: 'Zusammensetzung', quellen: ['Zusatzstoffe', 'Ernährungsphysiologische Zusatzstoffe', 'Additives'], ki: 'Zusatzstoffe je kg wie auf dem Etikett.' },
    { schluessel: 'fuetterungsempfehlung', name: 'Fütterungsempfehlung', typ: 'multi_line_text_field', gruppe: 'Fütterung', quellen: ['Fütterungsempfehlung', 'Fütterungshinweise', 'Feeding guide', 'Feeding instructions'], ki: 'Fütterungsempfehlung laut Etikett, z. B. Tagesmenge je Körpergewicht.' },
    ...grundpreis('Inhalt & Grundpreis', false),
    ...gpsr({ pflicht: true }),
  ],
};

/* ---------------------------------------------------------------- Lebensmittel & Getränke */

const HERKUNFT_UNTERNEHMER = 'Herkunft & Unternehmer';

const LEBENSMITTEL: BranchenVorlage = {
  branche: 'lebensmittel',
  gruppen: ['Produktdetails', 'Zutaten & Allergene', 'Nährwerte', 'Inhalt & Grundpreis', 'Lagerung & Haltbarkeit', HERKUNFT_UNTERNEHMER],
  regeln: { eanPflicht: true, ausrichtung: 'Lebensmittel und Getränke', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    verkehrsbezeichnung('Produktdetails', 'Rechtlich vorgeschriebene Bezeichnung laut Verpackung, z. B. „Vollmilchschokolade mit Haselnüssen“ – kein Fantasiename.'),
    geschmack('Produktdetails'),
    ernaehrung('Produktdetails'),
    { schluessel: 'oeko_kontrollstelle', name: 'Öko-Kontrollstelle', typ: 'single_line_text_field', gruppe: 'Produktdetails', quellen: ['Öko-Kontrollstelle', 'Kontrollstelle', 'Bio-Kontrollstelle', 'Organic control body'], ki: 'Code der Öko-Kontrollstelle, z. B. „DE-ÖKO-001“; nur bei Bio-Produkten.' },
    { schluessel: 'alkoholgehalt', name: 'Alkoholgehalt', typ: 'number_decimal', einheit: '% vol', gruppe: 'Produktdetails', quellen: ['Alkoholgehalt', 'Alkohol', 'Alcohol', 'ABV', 'Alcohol by volume'], ki: 'Alkoholgehalt in % vol bei Getränken über 1,2 % vol, nur die Zahl.' },
    {
      schluessel: 'altersfreigabe',
      name: 'Abgabe ab',
      typ: 'single_line_text_field',
      auswahl: ['16 Jahren', '18 Jahren'],
      werte: { '16': '16 Jahren', '18': '18 Jahren', 'ab 16': '16 Jahren', 'ab 18': '18 Jahren' },
      gruppe: 'Produktdetails',
      quellen: ['Altersfreigabe', 'Abgabe ab', 'Jugendschutz', 'Age restriction'],
      ki: 'Jugendschutz: Bier und Wein ab 16, Spirituosen und spirituosenhaltige Getränke ab 18 Jahren.',
    },
    { schluessel: 'pfand', name: 'Pfand', typ: 'number_decimal', einheit: '€', gruppe: 'Produktdetails', quellen: ['Pfand', 'Einwegpfand', 'Deposit', 'Bottle deposit'], ki: 'Pfandbetrag je Verkaufseinheit in Euro (wird neben dem Preis gesondert angegeben).' },
    zutaten('Zutaten & Allergene'),
    allergene('Zutaten & Allergene'),
    { schluessel: 'spuren', name: 'Kann Spuren enthalten von', typ: 'single_line_text_field', gruppe: 'Zutaten & Allergene', quellen: ['Spuren', 'Kann Spuren enthalten', 'May contain', 'Traces'], ki: 'Freiwilliger Spurenhinweis wörtlich von der Verpackung.' },
    ...naehrwerte('Nährwerte'),
    ...grundpreis('Inhalt & Grundpreis'),
    { schluessel: 'abtropfgewicht', name: 'Abtropfgewicht', typ: 'single_line_text_field', gruppe: 'Inhalt & Grundpreis', quellen: ['Abtropfgewicht', 'Drained weight'], ki: 'Abtropfgewicht mit Einheit bei Lebensmitteln in Aufgussflüssigkeit, z. B. „240 g“.' },
    lagerung('Lagerung & Haltbarkeit'),
    { schluessel: 'mindestrestlaufzeit', name: 'Mindesthaltbarkeit bei Versand', typ: 'number_integer', einheit: 'Tage', gruppe: 'Lagerung & Haltbarkeit', quellen: ['Restlaufzeit', 'Mindestrestlaufzeit', 'MHD Restlaufzeit', 'Minimum shelf life'], ki: 'Zugesicherte Mindesthaltbarkeit ab Versand in Tagen; das MHD selbst steht auf der Packung.' },
    herkunftsland(HERKUNFT_UNTERNEHMER, { ki: 'Ursprungsland, wenn angegeben (Pflicht z. B. bei Obst, Gemüse, Honig, Olivenöl, Fleisch).' }),
    lebensmittelunternehmer(HERKUNFT_UNTERNEHMER),
  ],
};

/* ---------------------------------------------------------------- Nahrungsergänzung & Sporternährung */

const NAHRUNGSERGAENZUNG: BranchenVorlage = {
  branche: 'nahrungsergaenzung',
  gruppen: ['Produktdetails', 'Nährstoffe & Verzehr', 'Zutaten & Allergene', 'Nährwerte', 'Inhalt & Grundpreis', HERKUNFT_UNTERNEHMER],
  regeln: { eanPflicht: true, ausrichtung: 'Nahrungsergänzungsmittel und Sporternährung', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    verkehrsbezeichnung('Produktdetails', 'Bezeichnung laut Verpackung, bei Nahrungsergänzungsmitteln mit den kennzeichnenden Nährstoffen, z. B. „Nahrungsergänzungsmittel mit Vitamin D3“.'),
    {
      schluessel: 'produktart',
      name: 'Produktart',
      typ: 'single_line_text_field',
      auswahl: ['Nahrungsergänzungsmittel', 'Proteinpulver', 'Riegel', 'Getränk', 'Sporternährung', 'Sonstiges Lebensmittel'],
      werte: { supplement: 'Nahrungsergänzungsmittel', 'food supplement': 'Nahrungsergänzungsmittel', nem: 'Nahrungsergänzungsmittel', protein: 'Proteinpulver', whey: 'Proteinpulver', bar: 'Riegel', drink: 'Getränk', 'sports nutrition': 'Sporternährung' },
      gruppe: 'Produktdetails',
      quellen: ['Produktart', 'Kategorie', 'Product type'],
      ki: 'Art des Produkts; „Nahrungsergänzungsmittel“ nur, wenn es so bezeichnet ist.',
    },
    {
      schluessel: 'darreichungsform',
      name: 'Darreichungsform',
      typ: 'single_line_text_field',
      auswahl: ['Kapseln', 'Tabletten', 'Pulver', 'Flüssig', 'Tropfen', 'Gummies', 'Sachets', 'Riegel'],
      werte: { capsules: 'Kapseln', caps: 'Kapseln', tablets: 'Tabletten', powder: 'Pulver', liquid: 'Flüssig', drops: 'Tropfen', gummy: 'Gummies', gummies: 'Gummies', sachet: 'Sachets', bar: 'Riegel' },
      gruppe: 'Produktdetails',
      quellen: ['Darreichungsform', 'Form', 'Dosage form'],
      ki: 'Darreichungsform.',
    },
    geschmack('Produktdetails'),
    ernaehrung('Produktdetails'),
    { schluessel: 'tagesdosis', name: 'Tagesdosis', typ: 'single_line_text_field', gruppe: 'Nährstoffe & Verzehr', pflicht: true, quellen: ['Tagesdosis', 'Tagesportion', 'Portion', 'Serving size', 'Daily dose'], ki: 'Empfohlene tägliche Verzehrmenge, z. B. „2 Kapseln“ oder „30 g“.' },
    { schluessel: 'naehrstoffe_tagesdosis', name: 'Nährstoffe je Tagesdosis', typ: 'multi_line_text_field', gruppe: 'Nährstoffe & Verzehr', pflicht: true, quellen: ['Nährstoffe je Tagesdosis', 'Nährstoffgehalt', 'Inhaltsstoffe je Tagesdosis', 'Supplement facts', 'Per daily dose'], ki: 'Jeder Nährstoff mit Menge je Tagesdosis und % des Referenzwerts (NRV), wörtlich aus der Tabelle; nie schätzen.' },
    { schluessel: 'verzehrempfehlung', name: 'Verzehrempfehlung', typ: 'multi_line_text_field', gruppe: 'Nährstoffe & Verzehr', pflicht: true, quellen: ['Verzehrempfehlung', 'Einnahmeempfehlung', 'Anwendung', 'Directions', 'Suggested use'], ki: 'Verzehrempfehlung wörtlich von der Verpackung.' },
    {
      schluessel: 'warnhinweise',
      name: 'Pflicht-Warnhinweise',
      typ: 'multi_line_text_field',
      gruppe: 'Nährstoffe & Verzehr',
      pflicht: true,
      quellen: ['Warnhinweise', 'Hinweise', 'Warnings'],
      ki: 'Hinweise nach NemV § 4 von der Verpackung: Tagesdosis nicht überschreiten, kein Ersatz für eine ausgewogene Ernährung, außerhalb der Reichweite kleiner Kinder lagern – plus produktspezifische Warnhinweise.',
    },
    { schluessel: 'portionen', name: 'Portionen je Packung', typ: 'number_integer', gruppe: 'Nährstoffe & Verzehr', quellen: ['Portionen', 'Portionen je Packung', 'Servings', 'Servings per container'], ki: 'Anzahl der Portionen bzw. Tagesdosen je Packung.' },
    zutaten('Zutaten & Allergene'),
    allergene('Zutaten & Allergene'),
    ...naehrwerte('Nährwerte', false),
    ...grundpreis('Inhalt & Grundpreis'),
    lagerung('Inhalt & Grundpreis'),
    herkunftsland(HERKUNFT_UNTERNEHMER),
    lebensmittelunternehmer(HERKUNFT_UNTERNEHMER),
  ],
};

/* ---------------------------------------------------------------- Drogerie & Gesundheit */

const DROGERIE: BranchenVorlage = {
  branche: 'drogerie',
  gruppen: ['Produktdetails', 'Inhaltsstoffe & Anwendung', 'Inhalt & Grundpreis', 'Gefahrstoffe', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: true, ausrichtung: 'Drogerie-, Haushalts- und Gesundheitsartikel', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    {
      schluessel: 'produktart',
      name: 'Produktart',
      typ: 'single_line_text_field',
      auswahl: ['Körperpflege', 'Haarpflege', 'Mundhygiene', 'Wasch- & Reinigungsmittel', 'Haushaltswaren', 'Baby & Kind', 'Gesundheit', 'Medizinprodukt', 'Hygiene', 'Sonstiges'],
      werte: { 'personal care': 'Körperpflege', 'hair care': 'Haarpflege', 'oral care': 'Mundhygiene', detergent: 'Wasch- & Reinigungsmittel', cleaning: 'Wasch- & Reinigungsmittel', household: 'Haushaltswaren', baby: 'Baby & Kind', health: 'Gesundheit', 'medical device': 'Medizinprodukt', hygiene: 'Hygiene' },
      gruppe: 'Produktdetails',
      quellen: ['Produktart', 'Kategorie', 'Product type'],
      ki: 'Art des Drogerieartikels.',
    },
    modell('Produktdetails', { ki: 'Produktlinie bzw. Name ohne Marke und Inhalt.' }),
    { schluessel: 'pzn', name: 'PZN', typ: 'single_line_text_field', gruppe: 'Produktdetails', quellen: ['PZN', 'Pharmazentralnummer'], ki: 'Pharmazentralnummer (8 Ziffern) bei apothekenüblichen Artikeln.' },
    { schluessel: 'duftrichtung', name: 'Duftrichtung', typ: 'single_line_text_field', gruppe: 'Produktdetails', quellen: ['Duftrichtung', 'Duft', 'Scent'], ki: 'Duftrichtung, z. B. „Lavendel“.' },
    {
      schluessel: 'eigenschaften',
      name: 'Eigenschaften',
      typ: 'list.single_line_text_field',
      auswahl: ['Vegan', 'Zertifizierte Naturkosmetik', 'Ohne Duftstoffe', 'Ohne Parabene', 'Ohne Mikroplastik', 'Dermatologisch getestet', 'Nachfüllpack', 'Umweltzeichen'],
      werte: { vegan: 'Vegan', 'fragrance free': 'Ohne Duftstoffe', 'paraben free': 'Ohne Parabene', refill: 'Nachfüllpack', ecolabel: 'Umweltzeichen', 'blauer engel': 'Umweltzeichen' },
      gruppe: 'Produktdetails',
      quellen: ['Eigenschaften', 'Besonderheiten', 'Features', 'Claims'],
      ki: 'Nur ausdrücklich angegebene Eigenschaften.',
    },
    inhaltsstoffe('Inhaltsstoffe & Anwendung', { name: 'Inhaltsstoffe', pflicht: true, ki: 'Bei Kosmetik die INCI-Liste, bei Wasch- und Reinigungsmitteln die Angaben nach Detergenzienverordnung – wörtlich, nie erfinden.' }),
    anwendung('Inhaltsstoffe & Anwendung', { name: 'Anwendung und Dosierung' }),
    pao('Inhaltsstoffe & Anwendung'),
    ...grundpreis('Inhalt & Grundpreis'),
    ...gefahrstoff('Gefahrstoffe'),
    ...gpsr({ pflicht: true }),
  ],
};

/* ---------------------------------------------------------------- Bücher, Medien & Büro */

const BUECHER: BranchenVorlage = {
  branche: 'buecher',
  gruppen: ['Zustand', 'Titel & Mitwirkende', 'Ausgabe', 'Medien & Büro', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: true, ausrichtung: 'Bücher, Medien und Bürobedarf', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    zustandWare('Zustand'),
    zustandBeschreibung('Zustand'),
    { schluessel: 'isbn', name: 'ISBN', typ: 'single_line_text_field', gruppe: 'Titel & Mitwirkende', quellen: ['ISBN', 'ISBN-13', 'ISBN-10', 'isbn'], ki: 'ISBN-13 ohne Bindestriche; ISBN-10 nur, wenn keine ISBN-13 vorhanden ist.' },
    { schluessel: 'autor', name: 'Autor / Urheber', typ: 'single_line_text_field', gruppe: 'Titel & Mitwirkende', quellen: ['Autor', 'Autorin', 'Verfasser', 'Author', 'Künstler', 'Interpret', 'Artist', 'Regie'], ki: 'Autor(en), Interpret bzw. Regie wie im Impressum; mehrere mit Komma.' },
    { schluessel: 'verlag', name: 'Verlag / Label', typ: 'single_line_text_field', gruppe: 'Titel & Mitwirkende', quellen: ['Verlag', 'Publisher', 'Label', 'Studio'], ki: 'Verlag, Label bzw. Studio.' },
    { schluessel: 'genre', name: 'Genre', typ: 'single_line_text_field', gruppe: 'Titel & Mitwirkende', quellen: ['Genre', 'Kategorie', 'Thema', 'Subject'], ki: 'Genre bzw. Thema, z. B. „Kriminalroman“.' },
    { schluessel: 'erscheinungsjahr', name: 'Erscheinungsjahr', typ: 'jahr', gruppe: 'Ausgabe', quellen: ['Erscheinungsjahr', 'Jahr', 'Erscheinungsdatum', 'Publication year', 'Year', 'Release date'], ki: 'Erscheinungsjahr dieser Ausgabe.' },
    { schluessel: 'auflage', name: 'Auflage', typ: 'single_line_text_field', gruppe: 'Ausgabe', quellen: ['Auflage', 'Ausgabe', 'Edition'], ki: 'Auflage bzw. Ausgabe, z. B. „3. Auflage“ oder „Erstausgabe“.' },
    {
      schluessel: 'sprache',
      name: 'Sprache',
      typ: 'single_line_text_field',
      auswahl: ['Deutsch', 'Englisch', 'Französisch', 'Spanisch', 'Italienisch', 'Niederländisch', 'Mehrsprachig', 'Sonstige'],
      werte: { german: 'Deutsch', de: 'Deutsch', english: 'Englisch', en: 'Englisch', french: 'Französisch', spanish: 'Spanisch', italian: 'Italienisch', dutch: 'Niederländisch', multilingual: 'Mehrsprachig' },
      gruppe: 'Ausgabe',
      quellen: ['Sprache', 'Language'],
      ki: 'Sprache des Inhalts.',
    },
    {
      schluessel: 'format',
      name: 'Format',
      typ: 'single_line_text_field',
      auswahl: ['Gebundene Ausgabe', 'Taschenbuch', 'Broschiert', 'E-Book', 'Hörbuch', 'CD', 'Schallplatte', 'DVD', 'Blu-ray', 'Videospiel', 'Kalender', 'Zeitschrift', 'Bürobedarf'],
      werte: { hardcover: 'Gebundene Ausgabe', gebunden: 'Gebundene Ausgabe', paperback: 'Taschenbuch', softcover: 'Broschiert', ebook: 'E-Book', audiobook: 'Hörbuch', vinyl: 'Schallplatte', lp: 'Schallplatte', game: 'Videospiel', calendar: 'Kalender', magazine: 'Zeitschrift', office: 'Bürobedarf' },
      gruppe: 'Ausgabe',
      quellen: ['Format', 'Einband', 'Binding', 'Medium', 'Media type'],
      ki: 'Format bzw. Medium.',
    },
    { schluessel: 'seitenzahl', name: 'Seitenzahl', typ: 'number_integer', einheit: 'Seiten', gruppe: 'Ausgabe', quellen: ['Seitenzahl', 'Seiten', 'Pages', 'Number of pages'], ki: 'Anzahl der Seiten.' },
    { schluessel: 'laufzeit', name: 'Laufzeit', typ: 'number_integer', einheit: 'min', gruppe: 'Medien & Büro', quellen: ['Laufzeit', 'Spieldauer', 'Running time', 'Duration'], ki: 'Laufzeit in Minuten (Film, Hörbuch, Musik).' },
    {
      schluessel: 'altersfreigabe',
      name: 'Altersfreigabe (FSK/USK)',
      typ: 'single_line_text_field',
      auswahl: ['FSK 0', 'FSK 6', 'FSK 12', 'FSK 16', 'FSK 18', 'USK 0', 'USK 6', 'USK 12', 'USK 16', 'USK 18'],
      werte: { 'fsk0': 'FSK 0', 'fsk6': 'FSK 6', 'fsk12': 'FSK 12', 'fsk16': 'FSK 16', 'fsk18': 'FSK 18', 'usk0': 'USK 0', 'usk6': 'USK 6', 'usk12': 'USK 12', 'usk16': 'USK 16', 'usk18': 'USK 18' },
      gruppe: 'Medien & Büro',
      quellen: ['Altersfreigabe', 'FSK', 'USK', 'Age rating'],
      ki: 'Kennzeichnung nach Jugendschutzgesetz bei Filmen und Spielen.',
    },
    {
      schluessel: 'papierformat',
      name: 'Papierformat',
      typ: 'single_line_text_field',
      auswahl: ['A3', 'A4', 'A5', 'A6', 'DIN lang', 'Letter'],
      gruppe: 'Medien & Büro',
      quellen: ['Papierformat', 'Format (Papier)', 'Paper size'],
      ki: 'Papierformat bei Bürobedarf.',
    },
    farbe('Medien & Büro'),
    ...gpsr(),
  ],
};

/* ---------------------------------------------------------------- Kunst, Sammlerstücke & Antiquitäten */

const KUNST: BranchenVorlage = {
  branche: 'kunst',
  gruppen: ['Zustand & Echtheit', 'Werk', 'Maße & Material', 'Herkunft', GRUPPE_SICHERHEIT],
  regeln: { eanPflicht: false, ausrichtung: 'Kunst, Antiquitäten und Sammlerstücke', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [
    {
      ...zustand('Zustand & Echtheit', ['Neuwertig', 'Sehr gut', 'Gut', 'Altersgemäße Gebrauchsspuren', 'Restaurierungsbedürftig']),
      werte: { mint: 'Neuwertig', 'like new': 'Neuwertig', 'very good': 'Sehr gut', good: 'Gut', 'signs of age': 'Altersgemäße Gebrauchsspuren', used: 'Altersgemäße Gebrauchsspuren', gebraucht: 'Altersgemäße Gebrauchsspuren', 'needs restoration': 'Restaurierungsbedürftig' },
      ki: 'Zustand nur aus ausdrücklichen Angaben übernehmen, nie raten; Restaurierungen in die Zustandsbeschreibung.',
    },
    zustandBeschreibung('Zustand & Echtheit'),
    {
      schluessel: 'zertifikat',
      name: 'Echtheitsnachweis',
      typ: 'list.single_line_text_field',
      auswahl: ['Echtheitszertifikat', 'Gutachten', 'Werkverzeichnis', 'Galerie- bzw. Kaufbeleg', 'Keine'],
      werte: { 'certificate of authenticity': 'Echtheitszertifikat', coa: 'Echtheitszertifikat', expertise: 'Gutachten', 'catalogue raisonné': 'Werkverzeichnis', 'catalogue raisonne': 'Werkverzeichnis', receipt: 'Galerie- bzw. Kaufbeleg', none: 'Keine' },
      gruppe: 'Zustand & Echtheit',
      quellen: ['Echtheitszertifikat', 'Zertifikat', 'Gutachten', 'Certificate', 'Authenticity'],
      ki: 'Mehrere Werte möglich; nur ausdrücklich genannte Nachweise.',
    },
    {
      schluessel: 'signatur',
      name: 'Signatur',
      typ: 'single_line_text_field',
      auswahl: ['Signiert', 'Monogrammiert', 'Gestempelt', 'Unsigniert'],
      werte: { signed: 'Signiert', handsigniert: 'Signiert', monogram: 'Monogrammiert', stamped: 'Gestempelt', unsigned: 'Unsigniert' },
      gruppe: 'Zustand & Echtheit',
      quellen: ['Signatur', 'Signiert', 'Signature', 'Signed'],
      ki: 'Art der Signatur.',
    },
    {
      schluessel: 'objektart',
      name: 'Objektart',
      typ: 'single_line_text_field',
      auswahl: ['Gemälde', 'Grafik / Druck', 'Zeichnung', 'Fotografie', 'Skulptur', 'Keramik & Porzellan', 'Glas', 'Antike Möbel', 'Silber', 'Münzen', 'Briefmarken', 'Bücher & Autographen', 'Design', 'Sammelkarten', 'Sonstiges'],
      werte: { painting: 'Gemälde', print: 'Grafik / Druck', lithograph: 'Grafik / Druck', drawing: 'Zeichnung', photograph: 'Fotografie', sculpture: 'Skulptur', ceramics: 'Keramik & Porzellan', porcelain: 'Keramik & Porzellan', glass: 'Glas', furniture: 'Antike Möbel', coins: 'Münzen', stamps: 'Briefmarken', 'trading cards': 'Sammelkarten' },
      gruppe: 'Werk',
      pflicht: true,
      quellen: ['Objektart', 'Kategorie', 'Gattung', 'Object type', 'Medium'],
      ki: 'Art des Objekts.',
    },
    { schluessel: 'kuenstler', name: 'Künstler / Hersteller', typ: 'single_line_text_field', gruppe: 'Werk', quellen: ['Künstler', 'Kuenstler', 'Urheber', 'Manufaktur', 'Artist', 'Maker'], ki: 'Künstler bzw. Manufaktur; Zuschreibungen („Umkreis von …“) nur, wenn ausdrücklich so angegeben.' },
    { schluessel: 'werktitel', name: 'Titel des Werks', typ: 'single_line_text_field', gruppe: 'Werk', quellen: ['Titel', 'Werktitel', 'Title', 'Title of work'], ki: 'Titel des Werks bzw. Objektbezeichnung.' },
    { schluessel: 'entstehungsjahr', name: 'Entstehungsjahr', typ: 'jahr', gruppe: 'Werk', quellen: ['Entstehungsjahr', 'Jahr', 'Datierung', 'Year', 'Date'], ki: 'Vierstelliges Jahr, wenn datiert; sonst die Epoche angeben.' },
    {
      schluessel: 'epoche',
      name: 'Epoche',
      typ: 'single_line_text_field',
      auswahl: ['Vor 1800', '1800–1899', '1900–1945', '1945–1979', '1980–1999', 'Ab 2000'],
      werte: { '19. jahrhundert': '1800–1899', '19th century': '1800–1899', jugendstil: '1900–1945', 'art deco': '1900–1945', 'art nouveau': '1900–1945', zeitgenössisch: 'Ab 2000', contemporary: 'Ab 2000' },
      gruppe: 'Werk',
      quellen: ['Epoche', 'Zeitraum', 'Period', 'Era'],
      ki: 'Entstehungszeitraum.',
    },
    { schluessel: 'stil', name: 'Stil / Richtung', typ: 'single_line_text_field', gruppe: 'Werk', quellen: ['Stil', 'Kunstrichtung', 'Style', 'Movement'], ki: 'Stil bzw. Kunstrichtung, z. B. „Expressionismus“, „Biedermeier“.' },
    { schluessel: 'technik', name: 'Technik', typ: 'single_line_text_field', gruppe: 'Werk', quellen: ['Technik', 'Technique', 'Medium'], ki: 'Technik, z. B. „Öl auf Leinwand“, „Radierung“.' },
    { schluessel: 'auflage', name: 'Auflage / Exemplar', typ: 'single_line_text_field', gruppe: 'Werk', quellen: ['Auflage', 'Exemplar', 'Edition', 'Nummerierung'], ki: 'Bei Editionen Exemplar und Auflage, z. B. „12/50“.' },
    ...masse('Maße & Material'),
    material('Maße & Material', { ki: 'Material bzw. Bildträger, z. B. „Bronze“, „Leinwand“, „Porzellan“.' }),
    {
      schluessel: 'rahmen',
      name: 'Rahmen',
      typ: 'single_line_text_field',
      auswahl: ['Gerahmt', 'Ungerahmt', 'Auf Keilrahmen'],
      werte: { framed: 'Gerahmt', unframed: 'Ungerahmt', stretched: 'Auf Keilrahmen' },
      gruppe: 'Maße & Material',
      quellen: ['Rahmen', 'Gerahmt', 'Frame', 'Framed'],
      ki: 'Rahmung bei Bildern.',
    },
    { schluessel: 'provenienz', name: 'Provenienz', typ: 'multi_line_text_field', gruppe: 'Herkunft', quellen: ['Provenienz', 'Herkunft', 'Provenance', 'Vorbesitz'], ki: 'Nachgewiesene Vorbesitzer, Ausstellungen und Literatur sachlich und nur aus den Daten.' },
    herkunftsland('Herkunft'),
    ...gpsr(),
  ],
};

const ALLGEMEIN: BranchenVorlage = {
  branche: 'allgemein',
  gruppen: [],
  regeln: { eanPflicht: true, ausrichtung: '', beschreibungMin: 0, markePflicht: false },
  buyBoxMerkmale: null,
  felder: [],
};

export const BRANCHEN_VORLAGEN: Record<Branche, BranchenVorlage> = {
  fashion: FASHION, schuhe: SCHUHE, uhren: UHREN, schmuck: SCHMUCK, beauty: BEAUTY,
  elektronik: ELEKTRONIK, auto: AUTO, moebel: MOEBEL, garten: GARTEN, sport: SPORT, outdoor: OUTDOOR, fahrrad: FAHRRAD,
  spielwaren: SPIELWAREN, tierbedarf: TIERBEDARF, lebensmittel: LEBENSMITTEL, nahrungsergaenzung: NAHRUNGSERGAENZUNG,
  drogerie: DROGERIE, buecher: BUECHER, kunst: KUNST, allgemein: ALLGEMEIN,
};

/* ---------------------------------------------------------------- Buy Box: Merkmal → Feldschlüssel */

/**
 * Welche Vorlagenfelder die Merkmale der Buy Box liefern (je Branche, Reihenfolge = Vorrang).
 * Die Marke kommt immer aus produkt.marke.
 */
export const BRANCHE_MERKMAL_FELDER: Record<Branche, Partial<Record<BuyBoxMerkmal, string[]>>> = {
  uhren: {
    modell: ['dxl.referenz', 'dxl.modell'],
    serie: ['dxl.serie'],
    farbe: ['dxl.zifferblattfarbe'],
    groesse: ['dxl.gehaeusedurchmesser'],
    zustand: ['dxl.zustand'],
    jahr: ['dxl.baujahr'],
  },
  schmuck: {
    modell: ['dxl.modell'],
    serie: ['dxl.serie'],
    farbe: ['dxl.material'],
    groesse: ['dxl.ringgroesse', 'dxl.laenge'],
    zustand: ['dxl.zustand'],
  },
  fashion: {
    modell: ['dxl.modell'],
    farbe: ['dxl.farbe'],
    groesse: ['dxl.groesse'],
    zustand: ['dxl.zustand'],
  },
  schuhe: { modell: ['dxl.modell'], farbe: ['dxl.farbe'], groesse: ['dxl.groesse'], zustand: ['dxl.zustand'] },
  beauty: { modell: ['dxl.modell'], farbe: ['dxl.farbton'], groesse: ['dxl.nettofuellmenge'] },
  elektronik: { modell: ['dxl.mpn', 'dxl.modell'], farbe: ['dxl.farbe'], groesse: ['dxl.speicherkapazitaet'], zustand: ['dxl.zustand'] },
  auto: { modell: ['dxl.mpn'], zustand: ['dxl.zustand'] },
  moebel: { modell: ['dxl.modell'], farbe: ['dxl.farbe'], zustand: ['dxl.zustand'] },
  garten: { modell: ['dxl.mpn', 'dxl.modell'], farbe: ['dxl.farbe'], zustand: ['dxl.zustand'] },
  sport: { modell: ['dxl.modell'], farbe: ['dxl.farbe'], groesse: ['dxl.groesse'], zustand: ['dxl.zustand'] },
  outdoor: { modell: ['dxl.modell'], farbe: ['dxl.farbe'], groesse: ['dxl.groesse'], zustand: ['dxl.zustand'] },
  fahrrad: { modell: ['dxl.modell'], jahr: ['dxl.modelljahr'], farbe: ['dxl.farbe'], groesse: ['dxl.rahmengroesse'], zustand: ['dxl.zustand'] },
  spielwaren: { modell: ['dxl.mpn'], farbe: ['dxl.farbe'], zustand: ['dxl.zustand'] },
  tierbedarf: { serie: ['dxl.geschmack'], groesse: ['dxl.nettofuellmenge'] },
  lebensmittel: { serie: ['dxl.geschmack'], groesse: ['dxl.nettofuellmenge'] },
  nahrungsergaenzung: { serie: ['dxl.geschmack'], groesse: ['dxl.nettofuellmenge'] },
  drogerie: { modell: ['dxl.modell'], groesse: ['dxl.nettofuellmenge'] },
  buecher: { modell: ['dxl.isbn'], serie: ['dxl.format'], jahr: ['dxl.erscheinungsjahr'], zustand: ['dxl.zustand'] },
  kunst: { modell: ['dxl.werktitel'], jahr: ['dxl.entstehungsjahr'], zustand: ['dxl.zustand'] },
  allgemein: {},
};

/**
 * Zuordnung über alle Branchen – Vorgabe für Marktplätze ohne Vorlage (Allgemein) und wo die Branche nicht bekannt ist.
 * Reihenfolge der Branchen wie bisher: Uhren, Schmuck, Fashion zuerst.
 */
export const MERKMAL_FELDER: Partial<Record<BuyBoxMerkmal, string[]>> = (() => {
  const aus: Partial<Record<BuyBoxMerkmal, string[]>> = {};
  for (const b of ['uhren', 'schmuck', 'fashion', ...BRANCHE_LISTE] as Branche[]) {
    for (const [m, felder] of Object.entries(BRANCHE_MERKMAL_FELDER[b]) as [BuyBoxMerkmal, string[]][]) {
      aus[m] = [...new Set([...(aus[m] ?? []), ...felder])];
    }
  }
  return aus;
})();

/**
 * Zuordnung Merkmal → Felder für einen Marktplatz: nur die Felder seiner Branche – sonst läse z. B. ein Mode-Marktplatz
 * das Material als Farbe (Schmuck). Felder anderer Vorlagen greifen danach nur noch über ihren Namen (farbe, groesse …).
 */
export function merkmalZuordnung(b: Branche): Partial<Record<BuyBoxMerkmal, string[]>> {
  return b === 'allgemein' ? MERKMAL_FELDER : BRANCHE_MERKMAL_FELDER[b];
}

/** Lesbare Merkmale der Buy-Box-Vorgabe einer Branche, z. B. „Marke, Referenz, Zifferblattfarbe …“ */
export function buyBoxMerkmalNamen(b: Branche): string[] {
  const v = BRANCHEN_VORLAGEN[b];
  return (v.buyBoxMerkmale ?? []).map((m) => {
    if (m === 'marke') return BUYBOX_MERKMALE.marke;
    const k = BRANCHE_MERKMAL_FELDER[b][m]?.[0]?.replace(`${VORLAGE_NAMESPACE}.`, '');
    return v.felder.find((f) => f.schluessel === k)?.name ?? BUYBOX_MERKMALE[m];
  });
}

/* ---------------------------------------------------------------- Antworten */

export interface BrancheAntwort {
  branche: Branche;
  /** Inhaber bzw. DXL dürfen die Branche wechseln */
  aenderbar: boolean;
  /** Felder der Vorlage dieses Marktplatzes */
  vorlageFelder: number;
}

export interface VorlageErgebnis {
  branche: Branche;
  /** neu angelegte Felder */
  neu: number;
  /** vorhandene Vorlagenfelder aktualisiert */
  aktualisiert: number;
  /** Felder einer anderen Vorlage, die nicht mehr freigegeben sind */
  ausgeblendet: number;
  /** Schlüssel, die schon als eigenes Feld oder Feld aus dem Shop bestehen und unverändert bleiben */
  uebersprungen: string[];
}

/**
 * Felder nach Gruppen ordnen – Reihenfolge der Gruppen wie in der Vorlage, Felder ohne bzw. mit fremder Gruppe
 * dahinter; „Weitere Angaben“ zuletzt. Innerhalb der Gruppe bleibt die Reihenfolge der Eingabe.
 */
export function felderGruppieren<T extends { gruppe?: string }>(felder: T[], gruppen: string[] = []): { gruppe: string; felder: T[] }[] {
  const nach = new Map<string, T[]>();
  for (const f of felder) {
    const g = (f.gruppe ?? '').trim() || GRUPPE_WEITERE;
    const l = nach.get(g) ?? [];
    l.push(f);
    nach.set(g, l);
  }
  const rang = (g: string) => (g === GRUPPE_WEITERE ? 10_000 : gruppen.indexOf(g) >= 0 ? gruppen.indexOf(g) : 1_000);
  return [...nach.entries()]
    .sort((a, b) => rang(a[0]) - rang(b[0]) || a[0].localeCompare(b[0], 'de'))
    .map(([gruppe, l]) => ({ gruppe, felder: l }));
}
